import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { formatDateDDMonYY } from "@/utils/dateFormat";
import { DateInputDDMonYY } from "@/components/DateInputDDMonYY";
import NegotiationTable from "@/components/NegotiationTable";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  ShoppingCart,
  Calendar,
  Loader2,
  FileText,
  Users,
  Trash2,
  Edit,
  Save,
  X,
  Sheet,
  Truck,
  Package,
  Download,
  MessageCircle,
  User,
  FileSpreadsheet,
  Check,
  Clock,
  Eye,
  Activity,
  ClipboardList,
  AlertCircle,
  MessageSquare,
  Send,
  CheckCircle,
  XCircle,
  TrendingUp,
  GripVertical,
  ArrowRight,
  RefreshCw,
  ChevronDown
} from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval, parseISO } from "date-fns";
import LedgerDialog from "@/components/LedgerDialog";
import CustomerActivityLog from "@/components/CustomerActivityLog";
import CustomerDashboard from "@/pages/CustomerDashboard";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Selected order for detail view
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // New order dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Order Tracking
  const [showTracking, setShowTracking] = useState(false);
  const [trackingData, setTrackingData] = useState([]);
  const [trackingView, setTrackingView] = useState('day');
  const [trackingDate, setTrackingDate] = useState(new Date());
  const [trackingLoading, setTrackingLoading] = useState(false);
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [estType, setEstType] = useState("EST");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [orderRemark, setOrderRemark] = useState("");
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerFocusIndex, setCustomerFocusIndex] = useState(0);
  const [orderItems, setOrderItems] = useState([
    { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
    { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
    { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
    { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
    { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
    { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
    { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
    { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
  ]);

  // Payment terms options (includes new received status options)
  
  // Stock filter state
  const [stockPopup, setStockPopup] = useState(null); // 'low' | 'high' | null
  const [stockPopupSku, setStockPopupSku] = useState('TIN');
  const [effectiveStock, setEffectiveStock] = useState([]);
  const [packSizes, setPackSizes] = useState({});
  
  // Print-only grade/SKU overrides (local state, NOT saved to DB)
  const [printGradeOverrides, setPrintGradeOverrides] = useState({});
  const paymentOptions = ["ADV", "RDAP", "DDAP", "DDAP RECVD", "REG"];
  const paymentConfirmationOptions = ["ADV", "RDAP", "DDAP", "DDAP RECVD", "REG", "Rdap Recvd", "Recvd"];
  
  // Dispatch dialog state
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [partialDispatchDialogOpen, setPartialDispatchDialogOpen] = useState(false);
  const [dispatchItems, setDispatchItems] = useState([]);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  // Next order number preview
  const [nextOrderNumber, setNextOrderNumber] = useState("");

  // Orders page active tab
  const [ordersPageTab, setOrdersPageTab] = useState("orders");
  const [enquiries, setEnquiries] = useState([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [counterRates, setCounterRates] = useState([]);
  const [counterNotes, setCounterNotes] = useState("");
  const [sendingCounter, setSendingCounter] = useState(false);

  // Grade column placement dialog state (for Google Sheets)
  const [gradeColumnDialog, setGradeColumnDialog] = useState(false);
  const [unmappedGrades, setUnmappedGrades] = useState([]);
  const [columnInputs, setColumnInputs] = useState({});
  const [placingGrades, setPlacingGrades] = useState(false);
  const [gradeColumnOrderId, setGradeColumnOrderId] = useState(null);

  // Edit mode for bill summary
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ est_type: "", payment_terms: "", items: [], remark: "" });

  // Post to sheets loading state
  const [postingToSheets, setPostingToSheets] = useState(false);
  
  // Track which orders have been posted to sheets
  const [postedToSheets, setPostedToSheets] = useState({});
  
  // Dispatching state
  const [dispatching, setDispatching] = useState(false);

  // Google Sheets repost dialog
  const [repostDialogOpen, setRepostDialogOpen] = useState(false);
  const [repostingOrder, setRepostingOrder] = useState(null);
  const [reposting, setReposting] = useState(false);

  // Remark inline edit
  const [editingRemark, setEditingRemark] = useState(false);
  const [remarkText, setRemarkText] = useState("");

  // Finalize deal -> Order dialog
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [finalizeDeal, setFinalizeDeal] = useState(null);
  const [finalizeItems, setFinalizeItems] = useState([]);
  const [finalizePayTerms, setFinalizePayTerms] = useState("");
  const [finalizeDispatchName, setFinalizeDispatchName] = useState("");
  const [finalizeNotes, setFinalizeNotes] = useState("");
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeEstimateType, setFinalizeEstimateType] = useState("");
  const [finalizeDiscountPerKg, setFinalizeDiscountPerKg] = useState(10);
  // Linked deal for creating orders from confirmed deals
  const [linkedDealId, setLinkedDealId] = useState(null);
  const [linkedDealEnquiry, setLinkedDealEnquiry] = useState("");
  const [selectedDeal, setSelectedDeal] = useState(null);

  // Market Trends state
  const [marketTrends, setMarketTrends] = useState([]);
  const [activeTrend, setActiveTrend] = useState("regular_trend");
  const [trendSearch, setTrendSearch] = useState("");
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [trendSkuFilter, setTrendSkuFilter] = useState("ALL");
  const [demandGrades, setDemandGrades] = useState({});
  const [editingTrendPrice, setEditingTrendPrice] = useState(null);
  const [trendDetailPopup, setTrendDetailPopup] = useState(null);

  // Dispatchable orders state
  const [dispatchableOrders, setDispatchableOrders] = useState([]);
  const [loadingDispatchable, setLoadingDispatchable] = useState(false);
  // Dispatchable drag-drop, partial dispatch, outstanding
  const [dragIdx, setDragIdx] = useState(null);
  const [savingPriority, setSavingPriority] = useState(false);
  const [partialOrder, setPartialOrder] = useState(null);
  const [partialItems, setPartialItems] = useState([]);
  const [partialDialogOpen, setPartialDialogOpen] = useState(false);
  const [sendingPartial, setSendingPartial] = useState(false);
  const [dispLedgerOpen, setDispLedgerOpen] = useState(false);
  const [dispLedgerParty, setDispLedgerParty] = useState('');
  // Enquiry side panel popups
  const [activityLogCustomer, setActivityLogCustomer] = useState(null);
  const [previewCustomer, setPreviewCustomer] = useState(null);
  // Combine threads
  const [combineMode, setCombineMode] = useState(false);
  const [combineSelected, setCombineSelected] = useState([]);
  // Deal reject with remark
  const [dealRejectOpen, setDealRejectOpen] = useState(false);
  const [dealRejectTarget, setDealRejectTarget] = useState(null);
  const [dealRejectRemark, setDealRejectRemark] = useState("");
  // Customer behaviour profile
  const [behaviourProfile, setBehaviourProfile] = useState(null);
  const [behaviourLoading, setBehaviourLoading] = useState(false);
  // Online customers (today's activity data)
  const [onlineCustomers, setOnlineCustomers] = useState({});
  // Login sessions popup (lightweight - only sessions, not full activity log)
  const [loginSessionsPopup, setLoginSessionsPopup] = useState(null);
  // Selected customer rate preference (auto-fetched for NegotiationTable)
  const [custRatePref, setCustRatePref] = useState('');
  // Full auto rates map for NegotiationTable (matches customer's New Order view)
  const [custAutoRates, setCustAutoRates] = useState(null);

  const fetchDispatchableOrders = async () => {
    setLoadingDispatchable(true);
    try {
      const res = await axios.get(`${API}/products/dispatchable-orders`);
      setDispatchableOrders(res.data || []);
    } catch (e) { toast.error("Failed to fetch dispatchable orders"); }
    finally { setLoadingDispatchable(false); }
  };

  // Dispatchable drag-drop handlers
  const handleDispDragStart = (idx) => setDragIdx(idx);
  const handleDispDragOver = (e) => e.preventDefault();
  const handleDispDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); return; }
    const newList = [...dispatchableOrders];
    const [moved] = newList.splice(dragIdx, 1);
    newList.splice(dropIdx, 0, moved);
    setDispatchableOrders(newList);
    setDragIdx(null);
    setSavingPriority(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.put(`${API}/orders/dispatch-priority`, { order_ids: newList.map(o => o.id), username: user.username || 'unknown' });
      await fetchDispatchableOrders();
    } catch (_e) { toast.error("Failed to save priority"); }
    finally { setSavingPriority(false); }
  };
  const dispSendToSort = async (orderId, orderNumber) => {
    // Check if posted to Google Sheets first
    const orderToCheck = dispatchableOrders.find(o => o.id === orderId);
    if (orderToCheck && !orderToCheck.posted_to_sheets) {
      const proceed = window.confirm(`Order ${orderNumber} has NOT been posted to Google Sheets yet.\n\nPost to Google Sheets before sending to Sort?\n\nClick OK to open order details (post first), or Cancel to proceed anyway.`);
      if (proceed) {
        // Open the order in main orders view to allow posting
        const fullOrder = orders.find(o => o.id === orderId);
        if (fullOrder) { setSelectedOrder(fullOrder); }
        return;
      }
    }
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: 'to_sort' });
      toast.success(`${orderNumber} sent to warehouse (To Sort)`);
      fetchDispatchableOrders();
    } catch (_e) { toast.error("Failed to send to sort"); }
  };
  const dispOpenPartial = (order) => {
    setPartialOrder(order);
    setPartialItems((order.items || []).map(item => ({
      grade: item.grade || '', sku: item.sku || 'TIN', qty: item.qty || 0,
      rate: item.rate || 0, dispatchQty: 0, maxQty: item.qty || 0
    })));
    setPartialDialogOpen(true);
  };
  const dispSubmitPartial = async () => {
    if (!partialOrder) return;
    const selected = partialItems.filter(i => i.dispatchQty > 0);
    if (selected.length === 0) { toast.error("Select at least one grade to dispatch"); return; }
    setSendingPartial(true);
    try {
      const res = await axios.post(`${API}/orders/${partialOrder.id}/partial-sort`, {
        dispatch_items: selected.map(i => ({ grade: i.grade, sku: i.sku, qty: i.dispatchQty, rate: i.rate }))
      });
      toast.success(res.data?.message || `${partialOrder.order_number} partial sort done`);
      setPartialDialogOpen(false);
      setPartialOrder(null);
      fetchDispatchableOrders();
    } catch (_e) { toast.error(_e.response?.data?.detail || "Partial sort failed"); }
    finally { setSendingPartial(false); }
  };

  // { type: 'trades'|'demand', grade, sku, data }

  const fetchMarketTrends = async () => {
    setLoadingTrends(true);
    try {
      const res = await axios.get(`${API}/market-trends`);
      setMarketTrends(res.data.trends || []);
      setActiveTrend(res.data.active_trend || "regular_trend");
      setDemandGrades(res.data.demand_grades || {});
    } catch (e) { toast.error("Failed to fetch market trends"); }
    finally { setLoadingTrends(false); }
  };

  const setActiveTrendOnServer = async (trend) => {
    try {
      await axios.put(`${API}/market-trends/active`, { trend });
      setActiveTrend(trend);
      toast.success(`Market trend set to ${trendLabels[trend]}`);
    } catch (e) { toast.error("Failed to update trend"); }
  };

  const saveHighTrendPrice = async (grade, sku, price) => {
    try {
      await axios.put(`${API}/market-trends/override`, { grade, sku, trend_type: "high_trend", price: parseFloat(price) });
      toast.success(`High trend updated for ${grade}`);
      setEditingTrendPrice(null);
      fetchMarketTrends();
    } catch (e) { toast.error("Failed to update price"); }
  };

  const trendLabels = {
    high_trend: "High Trend",
    regular_trend: "Regular Trend",
    weak_trend: "Weak Trend",
    low_trend: "Low Trend",
  };
  const trendColors = {
    high_trend: { bg: "bg-red-600", hover: "hover:bg-red-700", light: "bg-red-50 text-red-700", header: "bg-red-100 text-red-800" },
    regular_trend: { bg: "bg-blue-600", hover: "hover:bg-blue-700", light: "bg-blue-50 text-blue-700", header: "bg-blue-100 text-blue-800" },
    weak_trend: { bg: "bg-amber-500", hover: "hover:bg-amber-600", light: "bg-amber-50 text-amber-700", header: "bg-amber-100 text-amber-800" },
    low_trend: { bg: "bg-gray-600", hover: "hover:bg-gray-700", light: "bg-gray-50 text-gray-700", header: "bg-gray-200 text-gray-800" },
  };

  // Get unique SKUs from trends
  const trendSkus = [...new Set(marketTrends.map(t => t.sku))].sort();

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
    }
  }, []);

  const fetchEffectiveStock = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products/effective-stock`);
      setEffectiveStock(response.data || []);
    } catch (error) {
    }
  }, []);

  const fetchPackSizes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products/pack-sizes`);
      setPackSizes(response.data || {});
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchCustomers();
    fetchProducts();
    fetchEffectiveStock();
    fetchPackSizes();
    fetchMarketTrends();
    fetchEnquiries();
  }, [fetchOrders, fetchCustomers, fetchProducts]);

  const fetchTracking = useCallback(async () => {
    setTrackingLoading(true);
    try {
      const res = await axios.get(`${API}/orders/tracking`);
      setTrackingData(res.data || []);
    } catch (e) { }
    finally { setTrackingLoading(false); }
  }, []);

  useEffect(() => { if (showTracking) fetchTracking(); }, [showTracking, fetchTracking]);

  // Poll online customer activity every 30s when enquiry tab is active
  useEffect(() => {
    if (ordersPageTab !== 'enquiry') return;
    const pollActivity = async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const actRes = await axios.get(`${API}/customer/activity-log?date=${today}`);
        const map = {};
        const now = Date.now();
        const ACTIVE_WINDOW = 5 * 60 * 1000;
        for (const c of (actRes.data || [])) {
          if (c.login_count > 0 && c.activities?.length > 0) {
            const latestAct = c.activities[0];
            const latestTs = latestAct?.timestamp ? new Date(latestAct.timestamp).getTime() : 0;
            const isActive = (now - latestTs) < ACTIVE_WINDOW;
            map[c.customer_name] = {
              login_time: c.login_time, login_count: c.login_count, activities: c.activities || [],
              isActive, latestAction: latestAct?.action || '', latestDetails: latestAct?.details || '', latestTimestamp: latestAct?.timestamp || ''
            };
          }
        }
        setOnlineCustomers(map);
      } catch { /* */ }
    };
    const interval = setInterval(pollActivity, 30000);
    return () => clearInterval(interval);
  }, [ordersPageTab]);

  // Auto-fetch customer rate preference when enquiry is selected
  useEffect(() => {
    if (!selectedEnquiry?.customer_name) { setCustRatePref(''); setCustAutoRates(null); return; }
    const cname = selectedEnquiry.customer_name;
    const computeAutoRates = async () => {
      let ratePref = '';
      try {
        const bpRes = await axios.get(`${API}/customer/behaviour-profile?customer_name=${encodeURIComponent(cname)}`);
        ratePref = bpRes.data?.rate_preference || '';
        setCustRatePref(ratePref);
      } catch { setCustRatePref(''); }

      if (!marketTrends?.length) { setCustAutoRates(null); return; }

      // Get customer's last traded rates for regular_trend logic
      let customerLTP = {};
      try {
        const ordRes = await axios.get(`${API}/customer/my-orders?customer_name=${encodeURIComponent(cname)}`);
        for (const o of (ordRes.data || [])) {
          for (const item of (o.items || [])) {
            const k = `${item.grade}|${item.sku || 'TIN'}`;
            if (item.rate && !customerLTP[k]) customerLTP[k] = item.rate;
          }
        }
      } catch { /* */ }

      // Replicate computeTrendRates logic from CustomerDashboard
      const rateMap = {};
      for (const t of marketTrends) {
        const key = `${t.grade}|${t.sku}`;
        const colRate = t.customer_rate_na ? 0 : (t.customer_rate || 0);
        const custLastRate = customerLTP[key] || 0;

        if (t.customer_rate_na) { rateMap[key] = 0; continue; }

        if (ratePref) {
          if (ratePref === 'CR') rateMap[key] = colRate;
          else if (ratePref === 'BSR-1') rateMap[key] = colRate > 0 ? colRate + 100 : 0;
          else if (ratePref === 'BSR-2') rateMap[key] = colRate > 0 ? colRate + 200 : 0;
          else if (ratePref === 'BSR-3') rateMap[key] = colRate > 0 ? colRate + 300 : 0;
          else rateMap[key] = colRate || t.ltp || 0;
          continue;
        }

        if (activeTrend === 'high_trend') rateMap[key] = 0;
        else if (activeTrend === 'regular_trend') {
          const maxRate = Math.max(colRate, custLastRate);
          rateMap[key] = maxRate > 0 ? maxRate + 100 : 0;
        } else if (activeTrend === 'weak_trend') rateMap[key] = colRate;
        else if (activeTrend === 'low_trend') rateMap[key] = colRate > 100 ? colRate - 100 : colRate;
        else rateMap[key] = colRate || t.ltp || 0;
      }
      setCustAutoRates(Object.keys(rateMap).some(k => rateMap[k] > 0) ? rateMap : null);
    };
    computeAutoRates();
  }, [selectedEnquiry?.customer_name, marketTrends, activeTrend]);



  const getTrackingFiltered = () => {
    const now = trackingDate;
    let start, end;
    if (trackingView === 'day') {
      const ds = format(now, 'yyyy-MM-dd');
      return trackingData.filter(o => {
        const dates = [o.sent_to_sort_at, o.preparing_at, o.ready_at, o.dispatched_at].filter(Boolean);
        return dates.some(d => d.substring(0, 10) === ds);
      });
    } else if (trackingView === 'week') {
      start = startOfWeek(now, { weekStartsOn: 1 });
      end = endOfWeek(now, { weekStartsOn: 1 });
    } else {
      start = startOfMonth(now);
      end = endOfMonth(now);
    }
    return trackingData.filter(o => {
      const dates = [o.sent_to_sort_at, o.preparing_at, o.ready_at, o.sequenced_at, o.dispatched_at].filter(Boolean);
      return dates.some(d => {
        try { return isWithinInterval(parseISO(d), { start, end }); } catch { return false; }
      });
    });
  };

  const fmtTs = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return '-'; }
  };

  const fmtDuration = (from, to) => {
    if (!from || !to) return '-';
    try {
      const diff = new Date(to) - new Date(from);
      const hrs = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      if (hrs > 24) return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
      return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    } catch { return '-'; }
  };

  const isExcludedSku = (i) => { const s = (i.sku || i.packing || '').toUpperCase().replace(/ /g, '_'); return s.startsWith('VACUUM') || s === 'TIN_REJ'; };

  // Stock qty helper - returns EFFECTIVE stock (FG - ordered)
  const getStockQtyForItem = (grade, sku) => {
    if (!grade) return null;
    let eff = effectiveStock.find(e => e.grade === grade && e.sku === sku);
    if (!eff) eff = effectiveStock.find(e => e.grade === grade);
    if (eff) return eff.effective;
    // Fallback to FG stock if effective not loaded yet
    let product = products.find(p => p.grade === grade && p.sku === sku);
    if (!product) product = products.find(p => p.grade === grade);
    return product ? product.qty : 0;
  };

  // Get unique grades from products
  const uniqueGrades = [...new Set(products.map(p => p.grade))];
  const skuTypes = ["TIN", "BUCKET", "TIN_REJ", "VACUUM_10", "VACUUM_20"];

  // Low/High stock product lists
  const lowStockProducts = products.filter(p => p.qty <= 10 && p.qty >= 0);
  const highStockProducts = products.filter(p => p.qty > 100);

  // Filter orders - exclude warehouse orders (to_sort, ready_to_dispatch)
  // Sort orders by order_date in ASCENDING order (oldest first - 1, 2, 3...), then by order_number
  const filteredOrders = orders
    .filter(order => !['to_sort', 'ready_to_dispatch', 'dispatched'].includes(order.status))
    .sort((a, b) => {
      // Sort by order_date ascending (oldest first for sequential view 1, 2, 3...)
      const dateA = a.order_date || a.created_at?.substring(0, 10) || '';
      const dateB = b.order_date || b.created_at?.substring(0, 10) || '';
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      // If same date, sort by order_number ascending (E03001, E03002, E03003...)
      return (a.order_number || '').localeCompare(b.order_number || '');
    });

  // Get current logged-in user info for audit trail
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };

  // Move order to warehouse with audit trail
  const moveToWarehouse = async (orderId) => {
    try {
      // Check if order is posted to Google Sheets first
      const orderToCheck = orders.find(o => o.id === orderId) || selectedOrder;
      if (orderToCheck && !orderToCheck.posted_to_sheets && !postedToSheets[orderId]) {
        toast.error("Please post order to Google Sheets first before moving to Warehouse");
        return;
      }
      
      const user = getCurrentUser();
      await axios.put(`${API}/orders/${orderId}`, { 
        status: 'to_sort',
        user_id: user?.id,
        username: user?.username
      });
      toast.success("Order moved to warehouse");
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error("Failed to move order");
    }
  };

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'PENDING' },
    { value: 'send_pi', label: 'Send PI' },
    { value: 'pi_sent', label: 'PI Sent' },
    { value: 'waiting_for_payment', label: 'Waiting for Payment' },
    { value: 'payment_recvd', label: 'Payment Recvd' },
    { value: 'in_process', label: 'In Process' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'to_sort', label: 'TO SORT' },
  ];

  // Fetch next order number preview
  const fetchNextOrderNumber = async () => {
    try {
      const response = await axios.get(`${API}/orders/next-order-number`);
      setNextOrderNumber(response.data.next_order_number);
    } catch (error) {
      // error handled by toast
      setNextOrderNumber("");
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // Check if moving to "to_sort" - require Google Sheet posting first
      if (newStatus === 'to_sort') {
        // Get the order to check posted_to_sheets status
        const orderToCheck = orders.find(o => o.id === orderId) || selectedOrder;
        if (orderToCheck && !orderToCheck.posted_to_sheets && !postedToSheets[orderId]) {
          toast.error("Please post order to Google Sheets first before moving to To Sort");
          return;
        }
      }
      
      const resp = await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      
      if (newStatus === 'to_sort') {
        toast.success("Order moved to Warehouse - To Sort");
      } else {
        toast.success("Status updated");
      }
      
      // Show alerts for auto-added grade columns (when confirming triggers sheet post)
      if (resp.data?.added_column_alerts?.length > 0) {
        resp.data.added_column_alerts.forEach(alert => {
          toast.info(alert, { duration: 8000 });
        });
      }
      
      fetchOrders();
      // Update selected order with new status
      if (selectedOrder) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.label : status?.toUpperCase().replace(/_/g, ' ') || 'PENDING';
  };

  // Update payment terms directly
  const updatePaymentTerms = async (orderId, newTerms) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { payment_terms: newTerms });
      toast.success("Payment terms updated");
      fetchOrders();
      const updatedOrder = selectedOrder ? { ...selectedOrder, payment_terms: newTerms } : null;
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
        promptRepostToSheets(updatedOrder);
      }
    } catch (error) {
      toast.error("Failed to update payment terms");
    }
  };

  // Update order date directly
  const updateOrderDate = async (orderId, newDate) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { order_date: newDate });
      toast.success("Order date updated");
      fetchOrders();
      // Update selected order
      if (selectedOrder) {
        setSelectedOrder({ ...selectedOrder, order_date: newDate });
      }
    } catch (error) {
      toast.error("Failed to update order date");
    }
  };

  // Update discount per kg and recalculate
  const updateDiscount = async (orderId, discountPerKg) => {
    try {
      // Calculate new discount based on items
      const order = orders.find(o => o.id === orderId);
      if (!order) return;
      
      let totalDiscount = 0;
      const items = order.items || [];
      
      for (const item of items) {
        const qty = item.qty || 0;
        const pkgKg = getPackingKg(item);
        totalDiscount += discountPerKg * pkgKg * qty;
      }
      
      const invoiceValue = (order.total_value || 0) - totalDiscount;
      
      await axios.put(`${API}/orders/${orderId}`, { 
        discount_per_kg: discountPerKg,
        total_discount: totalDiscount,
        invoice_value: invoiceValue
      });
      toast.success("Discount updated");
      fetchOrders();
      const updatedOrder = selectedOrder ? { 
        ...selectedOrder, 
        discount_per_kg: discountPerKg,
        total_discount: totalDiscount,
        invoice_value: invoiceValue
      } : null;
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
        promptRepostToSheets(updatedOrder);
      }
    } catch (error) {
      toast.error("Failed to update discount");
    }
  };

  // Update created by directly
  const updateCreatedBy = async (orderId, newCreatedBy) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { created_by_username: newCreatedBy });
      toast.success("Created by updated");
      fetchOrders();
      // Update selected order
      if (selectedOrder) {
        setSelectedOrder({ ...selectedOrder, created_by_username: newCreatedBy });
      }
    } catch (error) {
      toast.error("Failed to update created by");
    }
  };

  // Force post order to Google Sheets
  const postOrderToSheets = async (orderId) => {
    if (!orderId) return;
    
    setPostingToSheets(true);
    try {
      const response = await axios.post(`${API}/orders/${orderId}/post-to-sheets`);
      toast.success(response.data.message || "Order posted to Google Sheets");
      // Show alerts for auto-added grade columns
      if (response.data.added_column_alerts?.length > 0) {
        response.data.added_column_alerts.forEach(alert => {
          toast.info(alert, { duration: 8000 });
        });
      }
      // Check for unmapped grades that need user input
      if (response.data.unmapped_grades?.length > 0) {
        setUnmappedGrades(response.data.unmapped_grades);
        setGradeColumnOrderId(orderId);
        setColumnInputs({});
        setGradeColumnDialog(true);
        toast.warning(`${response.data.unmapped_grades.length} grade(s) not found in sheet. Please assign column positions.`, { duration: 6000 });
      }
      // Mark this order as posted
      setPostedToSheets(prev => ({ ...prev, [orderId]: true }));
      // Update the order in state to reflect posted status
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, posted_to_sheets: true });
      }
      // Refresh orders to get updated posted_to_sheets status
      fetchOrders();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to post to Google Sheets";
      toast.error(errorMsg);
    } finally {
      setPostingToSheets(false);
    }
  };

  // Repost to Google Sheets after edit (with error popup)
  const repostToGoogleSheet = async () => {
    if (!repostingOrder) return;
    setReposting(true);
    try {
      const resp = await axios.post(`${API}/orders/${repostingOrder.id}/post-to-sheets`);
      if (repostingOrder.status === 'dispatched') {
        await axios.post(`${API}/orders/${repostingOrder.id}/post-dispatch-to-sheets`);
      }
      toast.success(`Order ${repostingOrder.order_number} posted to Google Sheet`);
      // Check for unmapped grades
      if (resp.data?.unmapped_grades?.length > 0) {
        setUnmappedGrades(resp.data.unmapped_grades);
        setGradeColumnOrderId(repostingOrder.id);
        setColumnInputs({});
        setGradeColumnDialog(true);
      }
      setPostedToSheets(prev => ({ ...prev, [repostingOrder.id]: true }));
      setRepostDialogOpen(false);
      setRepostingOrder(null);
      fetchOrders();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to post to Google Sheet. Please check Google Sheets settings.";
      toast.error(errorMsg, { duration: 8000 });
    } finally {
      setReposting(false);
    }
  };

  // Submit grade column placements to Google Sheets
  const submitGradeColumnPlacements = async () => {
    setPlacingGrades(true);
    let successCount = 0;
    for (const item of unmappedGrades) {
      const key = `${item.sku}-${item.grade}`;
      const colLetter = (columnInputs[key] || "").trim().toUpperCase();
      if (!colLetter) {
        toast.error(`Please enter column letter for ${item.grade} (${item.sku})`);
        setPlacingGrades(false);
        return;
      }
    }
    for (const item of unmappedGrades) {
      const key = `${item.sku}-${item.grade}`;
      const colLetter = columnInputs[key].trim().toUpperCase();
      try {
        const resp = await axios.post(`${API}/sheets/place-grade-column`, {
          grade: item.grade,
          sku: item.sku,
          column_letter: colLetter,
          order_id: gradeColumnOrderId
        });
        if (resp.data.success) {
          toast.success(`Grade '${item.grade}' placed at column ${colLetter}`);
          successCount++;
        }
      } catch (error) {
        toast.error(`Failed to place ${item.grade}: ${error.response?.data?.detail || 'Error'}`);
      }
    }
    setPlacingGrades(false);
    if (successCount === unmappedGrades.length) {
      setGradeColumnDialog(false);
      setUnmappedGrades([]);
      toast.success("All grades placed successfully! Re-post the order to update data.");
    }
  };


  // Order Enquiry functions
  const fetchEnquiries = async () => {
    try {
      const res = await axios.get(`${API}/customer/order-requests`);
      setEnquiries(res.data || []);
    } catch (e) { toast.error("Failed to load enquiries"); }
    // Fetch today's activity for online status
    try {
      const today = new Date().toISOString().slice(0, 10);
      const actRes = await axios.get(`${API}/customer/activity-log?date=${today}`);
      const map = {};
      const now = Date.now();
      const ACTIVE_WINDOW = 5 * 60 * 1000; // 5 minutes
      for (const c of (actRes.data || [])) {
        if (c.login_count > 0 && c.activities?.length > 0) {
          const latestAct = c.activities[0]; // sorted desc by timestamp
          const latestTs = latestAct?.timestamp ? new Date(latestAct.timestamp).getTime() : 0;
          const isActive = (now - latestTs) < ACTIVE_WINDOW;
          map[c.customer_name] = {
            login_time: c.login_time,
            login_count: c.login_count,
            activities: c.activities || [],
            isActive,
            latestAction: latestAct?.action || '',
            latestDetails: latestAct?.details || '',
            latestTimestamp: latestAct?.timestamp || ''
          };
        }
      }
      setOnlineCustomers(map);
    } catch { /* */ }
  };

  const openEnquiry = (req) => {
    setSelectedEnquiry(req);
    setCounterRates((req.items || []).map(i => ({
      grade: i.grade, sku: i.sku, qty: i.qty,
      customer_rate: i.customer_rate || 0,
      rate: i.customer_rate || 0
    })));
    setCounterNotes("");
  };

  const submitCounter = async () => {
    if (!selectedEnquiry) return;
    setSendingCounter(true);
    try {
      await axios.put(`${API}/order-requests/${selectedEnquiry.id}/counter`, {
        items: counterRates.map(i => ({ grade: i.grade, sku: i.sku, qty: i.qty, rate: parseFloat(i.rate) || 0 })),
        notes: counterNotes
      });
      toast.success("Counter rates sent to customer");
      setSelectedEnquiry(null);
      fetchEnquiries();
    } catch (e) { toast.error("Failed to send counter"); }
    finally { setSendingCounter(false); }
  };

  const confirmDeal = async (reqId) => {
    try {
      await axios.put(`${API}/order-requests/${reqId}/confirm`, { confirmed_by: "owner" });
      toast.success("Deal confirmed! Awaiting order creation.");
      fetchEnquiries();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Open the finalize dialog for a processing deal — uses the main Create Order form

  const openBehaviourProfile = async (customerName) => {
    setBehaviourLoading(true);
    try {
      const res = await axios.get(`${API}/customer/behaviour-profile?customer_name=${encodeURIComponent(customerName)}`);
      setBehaviourProfile(res.data);
    } catch { setBehaviourProfile({ customer_name: customerName, stats: {}, rate_preference: '', tags: [], notes: '', bill_type: '', payment_terms: '', show_highest_selling: false, show_discounted_grades: false, discounted_grades: [] }); }
    finally { setBehaviourLoading(false); }
  };
  const saveBehaviourProfile = async () => {
    if (!behaviourProfile) return;
    try { await axios.put(`${API}/customer/behaviour-profile`, behaviourProfile); } catch {}
  };

  const openFinalizeDialog = (deal) => {
    // Find customer from list
    const cust = customers.find(c => c.name === deal.customer_name);
    if (cust) {
      setSelectedCustomer(cust);
      if (cust.default_payment_terms) setPaymentTerms(cust.default_payment_terms);
    } else {
      // Fallback: create minimal customer object
      setSelectedCustomer({ id: deal.customer_id || '', name: deal.customer_name, phone: '', company: '' });
    }

    // Extract items from latest negotiation
    const latestItems = deal.latest_items || [];
    const negs = deal.negotiations || [];
    const lastItemRound = [...negs].reverse().find(n => n.items && n.items.length > 0 && !n.action);
    const itemsSource = lastItemRound?.items || latestItems;

    const prefilledItems = itemsSource.map(i => ({
      grade: i.grade || '',
      sku: i.sku || 'TIN',
      packing: '10',
      qty: String(i.qty || 0),
      rate: String(i.rate || i.customer_rate || 0),
      gradeSearch: i.grade || '',
      skuSearch: i.sku || 'TIN',
      gradeFocusIdx: 0,
      skuFocusIdx: 0
    }));

    // Pad with empty rows
    while (prefilledItems.length < 8) {
      prefilledItems.push({ grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 });
    }

    setOrderItems(prefilledItems);
    setLinkedDealId(deal.id);
    setLinkedDealEnquiry(deal.enquiry_number || '');
    setOrderDate(new Date().toISOString().split('T')[0]);
    setEstType("EST");
    setOrderRemark('');
    fetchNextOrderNumber();
    setDialogOpen(true);
  };

  const handleFinalizeOrder = async () => {
    if (!finalizeDeal) return;
    const validItems = finalizeItems.filter(i => i.grade && i.qty > 0 && i.rate > 0);
    if (validItems.length === 0) {
      toast.error("At least one item with Grade, Qty, and Rate is required");
      return;
    }
    
    // Calculate discount for ADV/DDAP
    const discountTerms = ['ADV', 'DDAP', 'DDAP RECVD'];
    const needsDiscount = discountTerms.includes(finalizePayTerms?.toUpperCase());
    let totalDiscount = 0;
    if (needsDiscount && finalizeDiscountPerKg > 0) {
      for (const item of validItems) {
        const pkgMap = { 'BUCKET': 10, 'TIN': 20, 'TIN_REJ': 20, 'VACUUM_10': 10, 'VACUUM_20': 20 };
        const pkgKg = pkgMap[item.sku] || 10;
        totalDiscount += finalizeDiscountPerKg * pkgKg * parseInt(item.qty);
      }
    }
    
    const totalValue = validItems.reduce((s, i) => s + (parseInt(i.qty) * parseFloat(i.rate)), 0);
    
    setFinalizing(true);
    try {
      const res = await axios.put(`${API}/order-requests/${finalizeDeal.id}/create-order`, {
        items: validItems.map(i => ({ grade: i.grade, sku: i.sku, qty: parseInt(i.qty), rate: parseFloat(i.rate) })),
        payment_terms: finalizePayTerms,
        dispatch_name: finalizeDispatchName,
        notes: finalizeNotes,
        estimate_type: finalizeEstimateType,
        discount_per_kg: needsDiscount ? finalizeDiscountPerKg : 0,
        total_discount: totalDiscount,
        invoice_value: totalValue - totalDiscount,
        created_by: (() => { try { return JSON.parse(localStorage.getItem('user'))?.username || 'admin'; } catch { return 'admin'; } })()
      });
      toast.success(res.data?.message || "Order created!");
      setFinalizeDialogOpen(false);
      setFinalizeDeal(null);
      fetchEnquiries();
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    } finally {
      setFinalizing(false);
    }
  };

  const renderNegotiationThread = (req, viewerRole) => {
    const negotiations = req.negotiations || [];
    const isActive = req.status === 'negotiating';
    const lastRound = negotiations[negotiations.length - 1];
    const waitingForOwner = lastRound?.from === 'customer';
    const waitingForCustomer = lastRound?.from === 'owner';
    
    return (
      <div key={req.id} className="border rounded-lg bg-white overflow-hidden mb-3" data-testid={`negotiation-${req.id}`}>
        <div className="p-2.5 bg-secondary/20 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className={`text-[9px] px-1.5 ${req.status === 'negotiating' ? 'bg-amber-100 text-amber-800' : req.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {req.status === 'negotiating' ? 'Negotiating' : req.status === 'confirmed' ? 'Confirmed' : 'Rejected'}
            </Badge>
            <span className="text-[10px] text-muted-foreground">{negotiations.length} round(s)</span>
            {req.order_number && <Badge variant="outline" className="text-[9px]">{req.order_number}</Badge>}
          </div>
          <span className="text-[10px] text-muted-foreground">{req.created_at?.slice(0, 10)}</span>
        </div>
        
        {/* All rounds */}
        <div className="p-2.5 space-y-2">
          {negotiations.map((round, ri) => (
            <div key={ri} className={`text-[11px] py-1.5 pl-3 border-l-2 rounded-r px-2 ${
              round.action === 'confirmed' ? 'border-l-green-500 bg-green-50/50' :
              round.action === 'rejected' ? 'border-l-red-400 bg-red-50/50' :
              round.from === 'customer' ? 'border-l-amber-400 bg-amber-50/30' : 'border-l-blue-400 bg-blue-50/30'
            }`}>
              <div className="flex justify-between mb-0.5">
                <span className="font-semibold">
                  {round.action === 'confirmed' ? '✓ Deal Confirmed' : round.action === 'rejected' ? '✗ Rejected' : round.from === 'customer' ? 'Customer' : 'Owner'} — R{round.round}
                </span>
                <span className="text-[9px] text-muted-foreground">{round.timestamp?.slice(11, 16)}</span>
              </div>
              {round.items && (
                <div className="flex flex-wrap gap-1">
                  {round.items.map((item, ii) => (
                    <span key={ii} className="bg-white/70 border px-1.5 py-0 rounded text-[10px]">
                      {item.grade} {item.sku} x{item.qty} {(item.rate || item.customer_rate) ? `@₹${item.rate || item.customer_rate}` : ''}
                    </span>
                  ))}
                </div>
              )}
              {round.notes && <p className="text-[10px] text-muted-foreground mt-0.5">{round.notes}</p>}
            </div>
          ))}
        </div>
        
        {/* Action area for active negotiations */}
        {isActive && viewerRole === 'owner' && waitingForOwner && (
          <div className="p-2.5 border-t bg-blue-50/30">
            <p className="text-[10px] font-bold mb-1.5">Your Counter:</p>
            {(lastRound?.items || []).map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-1">
                <span className="text-[11px] w-28 truncate">{item.grade} {item.sku} x{item.qty}</span>
                <Input type="number" placeholder="Rate" className="h-7 w-24 text-xs"
                  defaultValue={item.customer_rate || item.rate || ''}
                  onChange={e => {
                    const rate = parseFloat(e.target.value) || 0;
                    setCounterRates(prev => {
                      const u = [...prev];
                      const ei = u.findIndex(r => r.grade === item.grade && r.sku === (item.sku || 'TIN'));
                      if (ei >= 0) u[ei] = { ...u[ei], rate };
                      else u.push({ grade: item.grade, sku: item.sku || 'TIN', qty: item.qty, rate });
                      return u;
                    });
                  }}
                />
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <Button size="sm" className="flex-1 h-7 text-xs gap-1" disabled={sendingCounter}
                onClick={async () => {
                  const items = (lastRound?.items || []).map(item => {
                    const cr = counterRates.find(r => r.grade === item.grade && r.sku === (item.sku || 'TIN'));
                    return { grade: item.grade, sku: item.sku || 'TIN', qty: item.qty, rate: cr?.rate || item.rate || item.customer_rate || 0 };
                  });
                  setSendingCounter(true);
                  try {
                    await axios.put(`${API}/order-requests/${req.id}/counter`, { items, notes: '' });
                    toast.success("Counter sent"); fetchEnquiries();
                  } catch(e) { toast.error("Failed"); }
                  finally { setSendingCounter(false); }
                }}
                data-testid={`send-counter-${req.id}`}>
                <Send className="w-3 h-3" /> Counter
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-green-700 border-green-300"
                onClick={() => confirmDeal(req.id)} data-testid={`confirm-deal-${req.id}`}>
                <CheckCircle className="w-3 h-3" /> Confirm Deal
              </Button>
            </div>
          </div>
        )}
        {isActive && viewerRole === 'owner' && waitingForCustomer && (
          <div className="p-2 border-t bg-gray-50 text-center">
            <p className="text-[10px] text-muted-foreground">Waiting for customer response...</p>
          </div>
        )}
      </div>
    );
  };



  // Trigger repost dialog for an order
  const promptRepostToSheets = (order) => {
    setRepostingOrder(order);
    setRepostDialogOpen(true);
  };

  // Save remark inline
  const saveRemark = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { remark: remarkText });
      toast.success("Remark saved");
      setEditingRemark(false);
      const updatedOrder = { ...selectedOrder, remark: remarkText, notes: remarkText };
      setSelectedOrder(updatedOrder);
      fetchOrders();
      promptRepostToSheets(updatedOrder);
    } catch (error) {
      toast.error("Failed to save remark");
    }
  };

  // Dispatch order to warehouse (To Sort) with audit trail
  const handleDispatch = async () => {
    if (!selectedOrder) return;
    
    const user = getCurrentUser();
    
    setDispatching(true);
    try {
      await axios.post(`${API}/orders/${selectedOrder.id}/dispatch`, {
        user_id: user?.id,
        username: user?.username
      });
      toast.success("Order moved to Warehouse - To Sort");
      fetchOrders();
      setSelectedOrder(null);
      setDispatchDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to dispatch order");
    } finally {
      setDispatching(false);
    }
  };
  
  // Download order summary PDF (without stock column)
  const downloadOrderSummaryPdf = async () => {
    if (!selectedOrder) return;
    try {
      // Collect print overrides for this order's items
      const overrides = {};
      (selectedOrder.items || []).forEach((item, idx) => {
        const key = `${selectedOrder.id}_${idx}`;
        if (printGradeOverrides[key]) {
          overrides[idx] = printGradeOverrides[key];
        }
      });
      
      const response = await axios.post(`${API}/orders/${selectedOrder.id}/summary-pdf`, 
        { print_overrides: overrides },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `order_summary_${selectedOrder.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };
  
  // Share order summary on WhatsApp (without stock info)
  const shareOrderOnWhatsApp = async () => {
    if (!selectedOrder) return;
    try {
      const response = await axios.get(`${API}/orders/${selectedOrder.id}/whatsapp-summary`);
      const { message, customer_phone } = response.data;
      
      // Clean phone number
      const phone = customer_phone.replace('+', '').replace(/\s/g, '');
      
      // Encode message for URL
      const encodedMessage = encodeURIComponent(message);
      
      // Open WhatsApp
      window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
      toast.success("Opening WhatsApp...");
    } catch (error) {
      toast.error("Failed to generate WhatsApp message");
    }
  };

  // Open partial dispatch dialog
  const openPartialDispatchDialog = () => {
    if (!selectedOrder) return;
    
    // Initialize dispatch items with current order items
    const items = selectedOrder.items?.length > 0 
      ? selectedOrder.items.map(item => ({
          ...item,
          dispatch_qty: item.qty, // Default to full qty
          remaining_qty: 0
        }))
      : [{
          grade: selectedOrder.product_grade,
          sku: selectedOrder.packing,
          qty: selectedOrder.total_qty || selectedOrder.quantity_kg,
          rate: selectedOrder.unit_price,
          dispatch_qty: selectedOrder.total_qty || selectedOrder.quantity_kg,
          remaining_qty: 0
        }];
    
    setDispatchItems(items);
    setPartialDispatchDialogOpen(true);
  };

  // Update dispatch quantity for an item
  const updateDispatchQty = (index, dispatchQty) => {
    const newItems = [...dispatchItems];
    const originalQty = newItems[index].qty;
    const dQty = Math.min(Math.max(0, parseInt(dispatchQty) || 0), originalQty);
    newItems[index].dispatch_qty = dQty;
    newItems[index].remaining_qty = originalQty - dQty;
    setDispatchItems(newItems);
  };

  // Execute partial dispatch
  const handlePartialDispatch = async () => {
    if (!selectedOrder) return;
    
    // Validate at least some quantity is being dispatched
    const totalDispatch = dispatchItems.reduce((sum, item) => sum + (item.dispatch_qty || 0), 0);
    if (totalDispatch === 0) {
      toast.error("Please enter at least some quantity to dispatch");
      return;
    }
    
    setDispatching(true);
    try {
      const user = getCurrentUser();
      const dispatchPayload = {
        dispatch_items: dispatchItems.map(item => ({
          grade: item.grade || "",
          sku: item.sku || "",
          dispatch_qty: Number(item.dispatch_qty) || 0,
          remaining_qty: Number(item.remaining_qty) || 0,
          rate: Number(item.rate) || 0
        })),
        user_id: user?.id || "",
        username: user?.username || ""
      };
      
      const response = await axios.post(`${API}/orders/${selectedOrder.id}/partial-dispatch`, dispatchPayload);
      
      toast.success(response.data.message || "Partial dispatch successful");
      fetchOrders();
      setSelectedOrder(null);
      setPartialDispatchDialogOpen(false);
    } catch (error) {
      // error handled by toast
      toast.error(error.response?.data?.detail || "Failed to process partial dispatch");
    } finally {
      setDispatching(false);
    }
  };


  // Handle customer selection
  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
    // Set payment terms from customer's default (if available)
    if (customer?.default_payment_terms) {
      setPaymentTerms(customer.default_payment_terms);
    } else if (customer?.payment_type) {
      setPaymentTerms(customer.payment_type);
    }
    // Set bill type from customer's buyer_type (if available)
    if (customer?.buyer_type) {
      // Map buyer types to EST/INV if needed
      const buyerType = customer.buyer_type.toUpperCase();
      if (buyerType === 'RETAILER' || buyerType === 'DEALER') {
        setEstType('INV');
      }
    }
  };

  // Update item row - auto-add rows when needed
  const updateItemRow = (index, field, value) => {
    const newItems = [...orderItems];
    newItems[index][field] = value;
    
    // Count filled rows (rows with grade selected)
    const filledRows = newItems.filter(item => item.grade).length;
    
    // Auto-add 4 rows after 6 rows are filled
    if (field === 'grade' && value && filledRows === 6) {
      for (let i = 0; i < 4; i++) {
        newItems.push({ grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "" });
      }
    }
    // Auto-add 1 row if grade is selected on the last row
    else if (field === 'grade' && value && index === newItems.length - 1) {
      newItems.push({ grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "" });
    }
    
    setOrderItems(newItems);
  };

  // Add more rows manually
  const addMoreRows = () => {
    setOrderItems([
      ...orderItems,
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
    ]);
  };

  // Delete a specific row
  const deleteItemRow = (idx) => {
    if (orderItems.length <= 1) {
      // Keep at least one row, just clear it
      setOrderItems([{ grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 }]);
      return;
    }
    const newItems = orderItems.filter((_, i) => i !== idx);
    setOrderItems(newItems);
  };

  // Filter customers by search term
  const filteredCustomers = customers.filter(c => 
    customerSearch === "" || 
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.company?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone?.includes(customerSearch)
  );

  // Get effective stock quantity for a grade+sku combination
  const getStockQty = (grade, sku) => {
    if (!grade || !sku) return null;
    const eff = effectiveStock.find(e => e.grade === grade && e.sku === sku);
    if (eff) return eff.effective;
    const product = products.find(p => p.grade === grade && p.sku === sku);
    return product ? product.qty : 0;
  };

  // Handle customer selection with Enter key
  const handleCustomerKeyDown = (e) => {
    const visibleCustomers = filteredCustomers.slice(0, 10);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCustomerFocusIndex(prev => Math.min(prev + 1, visibleCustomers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCustomerFocusIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && visibleCustomers.length > 0 && !selectedCustomer) {
      e.preventDefault();
      const c = visibleCustomers[customerFocusIndex];
      if (c) {
        setSelectedCustomer(c);
        setCustomerSearch(c.name);
        if (c.default_payment_terms) setPaymentTerms(c.default_payment_terms);
        if (c.buyer_type) {
          const bt = c.buyer_type.toUpperCase();
          if (bt === 'RETAILER' || bt === 'DEALER') setEstType('INV');
        }
      }
    }
  };

  // Handle grade search with Enter key
  const handleGradeKeyDown = (e, idx, filteredGrades) => {
    const item = orderItems[idx];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      updateItemRow(idx, 'gradeFocusIdx', Math.min((item.gradeFocusIdx || 0) + 1, filteredGrades.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      updateItemRow(idx, 'gradeFocusIdx', Math.max((item.gradeFocusIdx || 0) - 1, 0));
    } else if (e.key === 'Enter' && filteredGrades.length > 0 && !item.grade) {
      e.preventDefault();
      const g = filteredGrades[item.gradeFocusIdx || 0];
      if (g) {
        updateItemRow(idx, 'grade', g);
        updateItemRow(idx, 'gradeSearch', g);
      }
    }
  };

  // Handle packing/sku search with Enter key
  const handleSkuKeyDown = (e, idx, filteredSkus) => {
    const item = orderItems[idx];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      updateItemRow(idx, 'skuFocusIdx', Math.min((item.skuFocusIdx || 0) + 1, filteredSkus.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      updateItemRow(idx, 'skuFocusIdx', Math.max((item.skuFocusIdx || 0) - 1, 0));
    } else if (e.key === 'Enter' && filteredSkus.length > 0 && !item.sku) {
      e.preventDefault();
      const s = filteredSkus[item.skuFocusIdx || 0];
      if (s) {
        updateItemRow(idx, 'sku', s);
        updateItemRow(idx, 'skuSearch', s);
      }
    }
  };

  // Calculate totals
  const NINE_KG_GRADES = ['JH', 'SJH', 'JJH', 'W150', 'W180', '180', 'A180', 'PG'];
  const getPackingKg = (item) => {
    const grade = (item.grade || '').toUpperCase().trim();
    const sku = (item.sku || item.packing || 'TIN').toUpperCase().trim();
    // First try exact grade+SKU from products DB
    if (packSizes[`${grade}|${sku}`]) return packSizes[`${grade}|${sku}`];
    // Then try grade-only fallback from DB
    if (packSizes[grade]) return packSizes[grade];
    // Final fallback: hardcoded list
    return NINE_KG_GRADES.includes(grade) ? 9 : 10;
  };
  const getOrderTotalKgs = (order) => {
    return (order?.items || []).filter(i => !isExcludedSku(i)).reduce((s, i) => s + ((i.qty || 0) * getPackingKg(i)), 0);
  };
  const getOrderAvrg = (order) => {
    const allItems = order?.items || [];
    const nonVac = allItems.filter(i => !isExcludedSku(i));
    const kgs = nonVac.reduce((s, i) => s + ((i.qty || 0) * getPackingKg(i)), 0);
    if (kgs <= 0) return 0;
    // Calculate value from filtered items only (exclude TIN_REJ, VACUUM)
    const filteredVal = nonVac.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
    if (order.total_discount > 0 && filteredVal > 0) {
      const allVal = allItems.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
      if (allVal > 0) {
        const discountShare = order.total_discount * (filteredVal / allVal);
        return (filteredVal - discountShare) / kgs;
      }
    }
    return filteredVal / kgs;
  };

  const validItems = orderItems.filter(item => item.grade && item.qty && item.rate);
  const totalQty = validItems.reduce((sum, item) => sum + parseFloat(item.qty || 0), 0);
  const totalValue = validItems.reduce((sum, item) => sum + (parseFloat(item.qty || 0) * parseFloat(item.rate || 0)), 0);
  const totalKgs = validItems.reduce((sum, item) => sum + (parseFloat(item.qty || 0) * getPackingKg(item)), 0);
  const nonVacItems = validItems.filter(i => !isExcludedSku(i));
  const nonVacKgs = nonVacItems.reduce((sum, i) => sum + (parseFloat(i.qty || 0) * getPackingKg(i)), 0);
  const nonVacVal = nonVacItems.reduce((sum, i) => sum + (parseFloat(i.qty || 0) * parseFloat(i.rate || 0)), 0);
  const formAvrg = nonVacKgs > 0 ? nonVacVal / nonVacKgs : 0;

  // Check for invalid product combinations (grade+sku that don't exist)
  const getInvalidItems = () => {
    const itemsWithGradeAndSku = orderItems.filter(item => item.grade && item.sku);
    const invalidItems = [];
    
    for (const item of itemsWithGradeAndSku) {
      const exists = products.some(p => 
        p.grade === item.grade && p.sku === item.sku
      );
      if (!exists) {
        invalidItems.push(`${item.grade}-${item.sku}`);
      }
    }
    return invalidItems;
  };
  
  const invalidItems = getInvalidItems();

  // Create order
  const handleCreateOrder = async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer");
      return;
    }
    if (validItems.length === 0) {
      toast.error("Please add at least one item with Grade, Qty and Rate");
      return;
    }
    
    // Block if there are invalid product combinations
    if (invalidItems.length > 0) {
      toast.error(`Invalid product combinations: ${invalidItems.join(', ')}. Please use only existing products.`);
      return;
    }

    try {
      const items = validItems.map(item => ({
        grade: item.grade,
        sku: item.sku,
        packing: `${item.packing} KG`,
        qty: parseFloat(item.qty),
        rate: parseFloat(item.rate),
        amount: parseFloat(item.qty) * parseFloat(item.rate)
      }));

      // Get current user for audit trail
      const user = getCurrentUser();

      const orderData = {
        customer_id: selectedCustomer.id,
        customer_name: selectedCustomer.name,
        dispatch_name: selectedCustomer.dispatch_name || '',
        customer_phone: selectedCustomer.phone,
        customer_company: selectedCustomer.company || selectedCustomer.billing_name,
        est_type: estType,
        payment_terms: paymentTerms,
        items: items,
        total_qty: totalQty,
        total_value: totalValue,
        remark: orderRemark,
        order_date: orderDate,
        // Audit trail - who created this order
        created_by: user?.id,
        created_by_username: user?.username
      };

      const res = await axios.post(`${API}/orders`, orderData);
      
      // If this order is from a confirmed deal, link it
      if (linkedDealId) {
        try {
          await axios.put(`${API}/order-requests/${linkedDealId}/mark-ordered`, {
            order_number: res.data?.order_number || ''
          });
        } catch (_) { /* non-critical: order created but deal link failed */ }
        setLinkedDealId(null);
        setLinkedDealEnquiry("");
        fetchEnquiries();
      }
      
      toast.success("Order created successfully");
      setDialogOpen(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    }
  };

  const resetForm = () => {
    setSelectedCustomer(null);
    setCustomerSearch("");
    setCustomerFocusIndex(0);
    setEstType("EST");
    setPaymentTerms("");
    setOrderRemark("");
    setOrderDate(new Date().toISOString().split('T')[0]);
    setOrderItems([
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
      { grade: "", sku: "", packing: "10", qty: "", rate: "", gradeSearch: "", skuSearch: "", gradeFocusIdx: 0, skuFocusIdx: 0 },
    ]);
  };

  const handleDeleteOrder = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!orderToDelete) return;
    try {
      await axios.delete(`${API}/orders/${orderToDelete.id}`);
      toast.success("Order deleted");
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to delete order");
    }
  };

  // Start editing
  const startEditing = () => {
    setEditData({
      est_type: selectedOrder.est_type || "EST",
      payment_terms: selectedOrder.payment_terms || "",
      items: selectedOrder.items || [],
      remark: ""
    });
    setIsEditing(true);
  };

  // Save edits
  const saveEdits = async () => {
    try {
      // Recalculate totals from edited items
      const totalQtyEdited = editData.items.reduce((sum, item) => sum + (item.qty || 0), 0);
      const totalValueEdited = editData.items.reduce((sum, item) => sum + (item.qty * item.rate || 0), 0);
      
      const updateData = {
        est_type: editData.est_type,
        payment_terms: editData.payment_terms,
        items: editData.items,
        total_qty: totalQtyEdited,
        total_value: totalValueEdited,
        edit_remark: editData.remark,
        edited_by: "Admin",
        edited_at: new Date().toISOString()
      };
      
      await axios.put(`${API}/orders/${selectedOrder.id}`, updateData);
      toast.success("Order updated");
      setIsEditing(false);
      
      const updatedOrder = { ...selectedOrder, ...updateData };
      setSelectedOrder(updatedOrder);
      fetchOrders();
      
      // Prompt to repost to Google Sheets
      promptRepostToSheets(updatedOrder);
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      send_pi: "bg-sky-100 text-sky-800",
      pi_sent: "bg-blue-100 text-blue-800",
      waiting_for_payment: "bg-amber-100 text-amber-800",
      payment_recvd: "bg-emerald-100 text-emerald-800",
      in_process: "bg-cyan-100 text-cyan-800",
      confirmed: "bg-green-100 text-green-800",
      order_confirmed: "bg-green-100 text-green-800",
      to_sort: "bg-orange-100 text-orange-800",
      sent_proforma: "bg-blue-100 text-blue-800",
      dispatched: "bg-purple-100 text-purple-800",
      delivered: "bg-teal-100 text-teal-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  // Calculate total order qty for display
  const totalOrderQty = filteredOrders.reduce((sum, order) => {
    if (order.items?.length > 0) {
      return sum + order.items.reduce((s, i) => s + (i.qty || 0), 0);
    }
    return sum + (order.total_qty || order.quantity_kg || 0);
  }, 0);

  const totalOrderKgs = filteredOrders.reduce((sum, order) => sum + getOrderTotalKgs(order), 0);
  const totalOrderValue = filteredOrders.reduce((sum, order) => {
    const allItems = order?.items || [];
    const nonVac = allItems.filter(i => !isExcludedSku(i));
    const filteredVal = nonVac.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
    if (order.total_discount > 0 && filteredVal > 0) {
      const allVal = allItems.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
      if (allVal > 0) return sum + filteredVal - (order.total_discount * (filteredVal / allVal));
    }
    return sum + filteredVal;
  }, 0);
  const dayAvrg = totalOrderKgs > 0 ? totalOrderValue / totalOrderKgs : 0;

  // Monthly Dispatch Average: find most recent month with dispatches (for previous month line)
  const allDispatched = orders.filter(o => o.status === 'dispatched' && o.dispatched_at);
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const curMonth = new Date().getMonth();
  const curYear = new Date().getFullYear();

  // Current month dispatch AVRG - value from filtered items only (exclude TIN_REJ, VACUUM)
  const getOrderFilteredVal = (o) => {
    const allItems = o?.items || [];
    const nonVac = allItems.filter(i => !isExcludedSku(i));
    const filteredVal = nonVac.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
    if (o.total_discount > 0 && filteredVal > 0) {
      const allVal = allItems.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
      if (allVal > 0) return filteredVal - (o.total_discount * (filteredVal / allVal));
    }
    return filteredVal;
  };
  const curMonthDispatched = allDispatched.filter(o => {
    const d = new Date(o.dispatched_at);
    return d.getMonth() === curMonth && d.getFullYear() === curYear;
  });
  const curMonthKgs = curMonthDispatched.reduce((sum, o) => sum + getOrderTotalKgs(o), 0);
  const curMonthVal = curMonthDispatched.reduce((sum, o) => sum + getOrderFilteredVal(o), 0);
  const curMonthAvrg = curMonthKgs > 0 ? curMonthVal / curMonthKgs : 0;
  const curMonthLabel = monthNames[curMonth];

  // Previous month dispatch AVRG (most recent month that is NOT current month)
  const prevMonthData = (() => {
    const prev = allDispatched.filter(o => {
      const d = new Date(o.dispatched_at);
      return !(d.getMonth() === curMonth && d.getFullYear() === curYear);
    });
    if (prev.length === 0) return { kgs: 0, val: 0, avrg: 0, label: '' };
    const monthMap = {};
    prev.forEach(o => {
      const d = new Date(o.dispatched_at);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (!monthMap[key]) monthMap[key] = { year: d.getFullYear(), month: d.getMonth(), orders: [] };
      monthMap[key].orders.push(o);
    });
    const sorted = Object.values(monthMap).sort((a, b) => b.year - a.year || b.month - a.month);
    const latest = sorted[0];
    const kgs = latest.orders.reduce((sum, o) => sum + getOrderTotalKgs(o), 0);
    const val = latest.orders.reduce((sum, o) => sum + getOrderFilteredVal(o), 0);
    return { kgs, val, avrg: kgs > 0 ? val / kgs : 0, label: `${monthNames[latest.month]}-${String(latest.year).slice(2)}` };
  })();

  return (
    <div className="p-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Orders</h1>
        <Button size="sm" variant="outline" onClick={() => setShowTracking(true)} className="gap-1 h-8" data-testid="order-tracking-btn">
          <Activity className="w-3.5 h-3.5" /> Order Tracking
        </Button>
      </div>

      {/* Tab switcher */}
      {(() => {
        let userRole = '';
        try { userRole = JSON.parse(localStorage.getItem('user') || '{}').role || ''; } catch {}
        const isAdmin = ['owner', 'admin'].includes(userRole);
        return (
      <div className="flex gap-1 bg-muted rounded-lg p-0.5 w-fit mb-4">
        <Button variant={ordersPageTab === 'orders' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs gap-1.5"
          onClick={() => setOrdersPageTab('orders')} data-testid="orders-tab-orders">
          <ShoppingCart className="w-3.5 h-3.5" /> Orders
        </Button>
        <Button variant={ordersPageTab === 'dispatchable' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs gap-1.5"
          onClick={() => { setOrdersPageTab('dispatchable'); fetchDispatchableOrders(); }} data-testid="orders-tab-dispatchable">
          <Truck className="w-3.5 h-3.5" /> Dispatchable
        </Button>
        {isAdmin && (
        <Button variant={ordersPageTab === 'market_trends' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs gap-1.5"
          onClick={() => { setOrdersPageTab('market_trends'); fetchMarketTrends(); }} data-testid="orders-tab-trends">
          <TrendingUp className="w-3.5 h-3.5" /> Market Trends
        </Button>
        )}
        {isAdmin && (
        <Button variant={ordersPageTab === 'enquiry' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs gap-1.5"
          onClick={() => { setOrdersPageTab('enquiry'); fetchEnquiries(); }} data-testid="orders-tab-enquiry">
          <MessageSquare className="w-3.5 h-3.5" /> Order Enquiry
          <Badge className="bg-red-500 text-white text-[9px] px-1.5 h-4">{enquiries.filter(e => e.status === 'negotiating').length}</Badge>
        </Button>
        )}
        {isAdmin && (
        <Button variant={ordersPageTab === 'confirmed_deals' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs gap-1.5"
          onClick={() => { setOrdersPageTab('confirmed_deals'); setSelectedDeal(null); fetchEnquiries(); }} data-testid="orders-tab-confirmed">
          <CheckCircle className="w-3.5 h-3.5" /> Confirmed Deals
          <Badge className="bg-green-500 text-white text-[9px] px-1.5 h-4">{enquiries.filter(e => ['processing', 'confirmed'].includes(e.status) && !e.order_number).length}</Badge>
        </Button>
        )}
        {isAdmin && (
        <Button variant={ordersPageTab === 'cancelled' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs gap-1.5"
          onClick={() => { setOrdersPageTab('cancelled'); setSelectedDeal(null); fetchEnquiries(); }} data-testid="orders-tab-cancelled">
          <XCircle className="w-3.5 h-3.5" /> Cancelled
        </Button>
        )}
      </div>
        );
      })()}

      {ordersPageTab === 'orders' && (<>
      {/* Orders Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Orders List - Left Side */}
        <Card className="lg:col-span-3">
          <CardContent className="p-0">
            {/* Header with New Order Button */}
            <div className="p-3 border-b bg-secondary/30 flex items-center justify-between">
              <div className="flex-1 mr-3">
                <div className="flex items-center gap-4">
                  <p className="text-sm font-medium">Orders ({filteredOrders.length}) - Qty: {totalOrderQty} {dayAvrg > 0 && <span className="ml-1">| Day AVRG: <span className="text-red-600 font-bold">{formatCurrency(Math.round(dayAvrg))}</span></span>}</p>
                  {curMonthAvrg > 0 && <p className="text-sm font-medium">{curMonthLabel}-{String(curYear).slice(2)} Dis AVRG: <span className="text-red-600 font-bold">{formatCurrency(Math.round(curMonthAvrg))}</span></p>}
                </div>
                {prevMonthData.avrg > 0 && <p className="text-[10px] text-emerald-700 font-medium">{prevMonthData.label} Dis AVRG: {formatCurrency(Math.round(prevMonthData.avrg))}</p>}
                <div className="flex gap-2 mt-1">
                  {lowStockProducts.length > 0 && (
                    <Button 
                      size="sm" variant={stockPopup === 'low' ? "default" : "outline"}
                      className={`h-5 text-[10px] px-2 gap-1 ${stockPopup === 'low' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                      onClick={() => setStockPopup(stockPopup === 'low' ? null : 'low')}
                      data-testid="orders-low-stock-btn"
                    >
                      <AlertCircle className="w-3 h-3" /> Low Stock ({lowStockProducts.length})
                    </Button>
                  )}
                  {highStockProducts.length > 0 && (
                    <Button 
                      size="sm" variant={stockPopup === 'high' ? "default" : "outline"}
                      className={`h-5 text-[10px] px-2 gap-1 ${stockPopup === 'high' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      onClick={() => setStockPopup(stockPopup === 'high' ? null : 'high')}
                      data-testid="orders-high-stock-btn"
                    >
                      <Package className="w-3 h-3" /> High Stock ({highStockProducts.length})
                    </Button>
                  )}
                </div>
                {/* Stock Grade Popup */}
                {stockPopup && (() => {
                  const allItems = stockPopup === 'low' ? lowStockProducts : highStockProducts;
                  const skuOrder = ['TIN', 'BUCKET', 'TIN_REJ', 'VACUUM_10', 'VACUUM_20'];
                  const skuLabels = { TIN: 'TIN', BUCKET: 'BUCKET', TIN_REJ: 'TIN REJ', VACUUM_10: 'VACUUM 10', VACUUM_20: 'VACUUM 20' };
                  const availableSkus = skuOrder.filter(s => allItems.some(p => p.sku === s));
                  const activeSku = availableSkus.includes(stockPopupSku) ? stockPopupSku : (availableSkus[0] || 'TIN');
                  const filtered = allItems.filter(p => p.sku === activeSku);
                  return (
                  <div className="mt-1 p-2 border rounded bg-white shadow-lg max-h-56 overflow-y-auto">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-bold ${stockPopup === 'low' ? 'text-amber-700' : 'text-green-700'}`}>
                        {stockPopup === 'low' ? 'Low Stock Grades' : 'High Stock Grades'}
                      </span>
                      <Button size="sm" variant="ghost" className="h-4 w-4 p-0" onClick={() => setStockPopup(null)}><X className="w-3 h-3" /></Button>
                    </div>
                    <div className="flex gap-1 mb-1 flex-wrap">
                      {availableSkus.map(s => (
                        <Button key={s} size="sm" variant={activeSku === s ? "default" : "outline"}
                          className={`h-5 text-[9px] px-2 ${activeSku === s ? '' : ''}`}
                          onClick={() => setStockPopupSku(s)}
                        >
                          {skuLabels[s] || s} ({allItems.filter(p => p.sku === s).length})
                        </Button>
                      ))}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/30">
                          <TableHead className="text-[9px] py-0.5 px-1 font-bold">Grade</TableHead>
                          <TableHead className="text-[9px] py-0.5 px-1 text-right font-bold">Qty</TableHead>
                          <TableHead className="text-[9px] py-0.5 px-1 text-right font-bold">Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((p, i) => (
                          <TableRow key={p.id || i} className="hover:bg-muted/30">
                            <TableCell className="text-[10px] py-0.5 px-1 font-medium">{p.grade}</TableCell>
                            <TableCell className={`text-[10px] py-0.5 px-1 text-right font-bold ${stockPopup === 'low' ? 'text-amber-600' : 'text-green-600'}`}>{p.qty}</TableCell>
                            <TableCell className="text-[10px] py-0.5 px-1 text-right">{p.base_price ? formatCurrency(p.base_price) : '-'}</TableCell>
                          </TableRow>
                        ))}
                        {filtered.length === 0 && (
                          <TableRow><TableCell colSpan={3} className="text-[10px] py-2 text-center text-muted-foreground">No grades in this SKU</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  );
                })()}
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => { 
                setDialogOpen(open); 
                if (open) {
                  // Reset order date to today when dialog opens
                  setOrderDate(new Date().toISOString().split('T')[0]);
                  // Fetch next order number preview
                  fetchNextOrderNumber();
                } else {
                  resetForm();
                  setLinkedDealId(null);
                  setLinkedDealEnquiry("");
                }
              }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="gap-1 h-7 text-xs">
                    <Plus className="w-3 h-3" />
                    New Order
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>

            {/* Column Headers - Order Date, Order No, Customer, Qty, Payment Terms, Created By */}
            <div className="grid grid-cols-[0.7fr_0.7fr_1.3fr_0.5fr_0.5fr_0.6fr_0.6fr_0.7fr] gap-2 px-3 py-2 bg-secondary/50 text-xs font-semibold text-muted-foreground border-b">
              <span>Date</span>
              <span>Order No.</span>
              <span>Customer</span>
              <span className="text-center">Qty</span>
              <span className="text-center text-blue-700">AVRG</span>
              <span className="text-center">Pay Terms</span>
              <span className="text-center">Created By</span>
              <span className="text-center">Status</span>
            </div>

            {/* Orders List */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : filteredOrders.length > 0 ? (
              <div className="max-h-[calc(100vh-350px)] overflow-y-auto">
                {filteredOrders.map((order) => (
                  <div
                    key={order.id}
                    onClick={() => { setSelectedOrder(order); setIsEditing(false); setEditingRemark(false); }}
                    className={`grid grid-cols-[0.7fr_0.7fr_1.3fr_0.5fr_0.5fr_0.6fr_0.6fr_0.7fr] gap-2 px-3 py-2 border-b cursor-pointer transition-colors text-xs ${
                      selectedOrder?.id === order.id ? 'bg-primary/10 border-l-4 border-l-primary' : 'hover:bg-secondary/50'
                    } ${(order.remark || order.notes) ? 'border-l-4 border-l-orange-400 bg-orange-50/30' : ''}`}
                  >
                    <span className="text-muted-foreground">{formatDateDDMonYY(order.order_date || order.created_at)}</span>
                    <span className="font-mono font-semibold">{order.order_number}</span>
                    <span className="truncate">{order.customer_name}</span>
                    <span className="text-center font-semibold">{order.total_qty || order.quantity_kg || '-'}</span>
                    <span className="text-center font-medium text-blue-700" title={`${getOrderTotalKgs(order)} kg | AVRG: ${formatCurrency(Math.round(getOrderAvrg(order)))}`}>{Math.round(getOrderAvrg(order)) || '-'}</span>
                    <span className="text-center text-muted-foreground">{order.payment_terms || '-'}</span>
                    <span className="text-center text-blue-600 font-medium truncate">{order.created_by_username || '-'}</span>
                    <span className="text-center">
                      <Badge className={`text-[10px] px-1.5 ${getStatusColor(order.status)}`}>{getStatusLabel(order.status)}</Badge>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <ShoppingCart className="w-12 h-12 mb-3 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">No orders found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Detail - Right Side */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            {selectedOrder ? (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg font-mono">{selectedOrder.order_number}</h3>
                  <div className="flex gap-1">
                    {/* Download PDF Button */}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7" 
                      onClick={downloadOrderSummaryPdf}
                      title="Download PDF"
                    >
                      <Download className="w-3.5 h-3.5 text-blue-600" />
                    </Button>
                    {/* Share on WhatsApp Button */}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7" 
                      onClick={shareOrderOnWhatsApp}
                      title="Share on WhatsApp"
                    >
                      <MessageCircle className="w-3.5 h-3.5 text-green-500" />
                    </Button>
                    {/* Post to Google Sheets */}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7" 
                      onClick={() => postOrderToSheets(selectedOrder.id)}
                      disabled={postingToSheets}
                      title={selectedOrder.posted_to_sheets || postedToSheets[selectedOrder.id] ? "Posted to Google Sheets (Click to re-post)" : "Post to Google Sheets"}
                    >
                      {postingToSheets ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Sheet className={`w-3.5 h-3.5 ${selectedOrder.posted_to_sheets || postedToSheets[selectedOrder.id] ? 'text-green-600' : 'text-red-500'}`} />
                      )}
                    </Button>
                    {!isEditing ? (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={startEditing}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                    ) : (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={saveEdits}>
                          <Save className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setIsEditing(false)}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteOrder(selectedOrder)}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>

                {/* Bill Type & Payment Terms - Editable */}
                <div className="grid grid-cols-1 gap-1.5 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                  {/* Order Date - Editable */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold w-24 bg-amber-200 px-2 py-0.5">ORDER DATE</span>
                    <DateInputDDMonYY
                      value={selectedOrder.order_date || ''} 
                      onChange={(e) => updateOrderDate(selectedOrder.id, e.target.value)}
                      className="h-6 text-[10px] flex-1 bg-white"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold w-24 bg-amber-200 px-2 py-0.5">BILL TYPE</span>
                    {isEditing ? (
                      <Select value={editData.est_type} onValueChange={(v) => setEditData({...editData, est_type: v})}>
                        <SelectTrigger className="h-6 text-[10px] flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EST">EST</SelectItem>
                          <SelectItem value="INV">INV</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-[10px] font-medium bg-amber-100 px-2 py-0.5 flex-1">{selectedOrder.est_type || "EST"}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold w-24 bg-amber-200 px-2 py-0.5">PAY TERMS</span>
                    <Select 
                      value={selectedOrder.payment_terms || ''} 
                      onValueChange={(v) => updatePaymentTerms(selectedOrder.id, v)}
                    >
                      <SelectTrigger className="h-6 text-[10px] flex-1 bg-white">
                        <SelectValue placeholder="Select">{selectedOrder.payment_terms || "-"}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {paymentConfirmationOptions.map((opt) => (
                          <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Order Status */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold w-24 bg-amber-200 px-2 py-0.5">STATUS</span>
                    <Select 
                      value={selectedOrder.status || 'pending'} 
                      onValueChange={(v) => updateOrderStatus(selectedOrder.id, v)}
                    >
                      <SelectTrigger className="h-6 text-[10px] flex-1 bg-white">
                        <SelectValue>{getStatusLabel(selectedOrder.status)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Action Dropdown for Dispatch */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold w-24 bg-green-200 px-2 py-0.5">ACTION</span>
                    <Select 
                      value="" 
                      onValueChange={(v) => {
                        if (v === 'dispatch') {
                          setDispatchDialogOpen(true);
                        } else if (v === 'partial_dispatch') {
                          openPartialDispatchDialog();
                        }
                      }}
                    >
                      <SelectTrigger className="h-6 text-[10px] flex-1 bg-white">
                        <SelectValue placeholder="Select Action" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dispatch" className="text-xs">
                          <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Dispatch (Full)</span>
                        </SelectItem>
                        <SelectItem value="partial_dispatch" className="text-xs">
                          <span className="flex items-center gap-1"><Package className="w-3 h-3" /> Partial Dispatch</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {isEditing && (
                    <div className="flex items-start gap-2 mt-1">
                      <span className="text-[10px] font-semibold w-24 bg-blue-200 px-2 py-0.5">EDIT NOTE</span>
                      <Input
                        value={editData.remark}
                        onChange={(e) => setEditData({...editData, remark: e.target.value})}
                        placeholder="Reason for edit..."
                        className="h-6 text-[10px] flex-1"
                      />
                    </div>
                  )}
                  {/* Order Remark - Always visible & editable */}
                  <div className="flex items-start gap-2 mt-1">
                    <span className="text-[10px] font-semibold w-24 bg-orange-200 px-2 py-0.5">REMARK</span>
                    <div className="flex-1 flex gap-1">
                      {editingRemark ? (
                        <>
                          <Input
                            value={remarkText}
                            onChange={(e) => setRemarkText(e.target.value)}
                            placeholder="Add remark..."
                            className="h-6 text-[10px] flex-1"
                            data-testid="remark-edit-input"
                            autoFocus
                          />
                          <Button size="sm" className="h-6 px-2 text-[10px] bg-green-600 hover:bg-green-700" onClick={() => saveRemark(selectedOrder.id)} data-testid="remark-save-btn">
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setEditingRemark(false)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      ) : (
                        <div 
                          className={`flex-1 text-[10px] px-2 py-0.5 rounded cursor-pointer min-h-[24px] flex items-center ${(selectedOrder.remark || selectedOrder.notes) ? 'bg-orange-50 border border-orange-300 text-orange-900 font-medium' : 'bg-gray-50 border border-dashed border-gray-300 text-gray-400'}`}
                          onClick={() => { setRemarkText(selectedOrder.remark || selectedOrder.notes || ''); setEditingRemark(true); }}
                          data-testid="remark-display-click"
                        >
                          {(selectedOrder.remark || selectedOrder.notes) || 'Click to add remark...'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Edit Remark Display */}
                {selectedOrder.edit_remark && (
                  <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2">
                    <span className="font-semibold">Last Edit: </span>
                    {selectedOrder.edit_remark} 
                    <span className="text-muted-foreground ml-2">
                      by {selectedOrder.edited_by} on {new Date(selectedOrder.edited_at).toLocaleString()}
                    </span>
                  </div>
                )}

                {/* Items Table - Editable */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-green-100">
                        <TableHead className="text-[10px] py-1.5 font-bold w-8 px-1">SR</TableHead>
                        <TableHead className="text-[10px] py-1.5 font-bold px-1">GRADES</TableHead>
                        <TableHead className="text-[10px] py-1.5 font-bold px-1">TYPE</TableHead>
                        <TableHead className="text-[10px] py-1.5 font-bold text-center px-1">STOCK</TableHead>
                        <TableHead className="text-[10px] py-1.5 font-bold text-right px-1">QTY</TableHead>
                        <TableHead className="text-[10px] py-1.5 font-bold text-right px-1">RATE</TableHead>
                        <TableHead className="text-[10px] py-1.5 font-bold text-right px-1">VALUE</TableHead>
                        {isEditing && <TableHead className="text-[10px] py-1.5 font-bold text-center px-1 w-6"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isEditing ? (
                        // Editable rows
                        editData.items.map((item, idx) => {
                          const stockQty = getStockQtyForItem(item.grade, item.sku);
                          return (
                          <TableRow key={idx}>
                            <TableCell className="text-[10px] py-0.5 font-medium px-1">{idx + 1}</TableCell>
                            <TableCell className="text-[10px] py-0.5 px-1">
                              <Select 
                                value={item.grade} 
                                onValueChange={(v) => {
                                  const newItems = [...editData.items];
                                  newItems[idx].grade = v;
                                  setEditData({...editData, items: newItems});
                                }}
                              >
                                <SelectTrigger className="h-6 text-[10px] w-16">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-48">
                                  {uniqueGrades.map((g) => (
                                    <SelectItem key={g} value={g} className="text-xs">{g}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-[10px] py-0.5 px-1">
                              <Select 
                                value={item.sku || 'TIN'} 
                                onValueChange={(v) => {
                                  const newItems = [...editData.items];
                                  newItems[idx].sku = v;
                                  setEditData({...editData, items: newItems});
                                }}
                              >
                                <SelectTrigger className="h-6 text-[10px] w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {skuTypes.map((s) => (
                                    <SelectItem key={s} value={s} className="text-xs">{s.replace('_', ' ')}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-[10px] py-0.5 text-center px-1">
                              <span className={`font-semibold ${stockQty > 100 ? 'text-green-600' : stockQty > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                                {stockQty !== null ? stockQty : '-'}
                              </span>
                            </TableCell>
                            <TableCell className="text-[10px] py-0.5 text-right px-1">
                              <Input
                                type="number"
                                value={item.qty}
                                onChange={(e) => {
                                  const newItems = [...editData.items];
                                  newItems[idx].qty = parseFloat(e.target.value) || 0;
                                  newItems[idx].amount = newItems[idx].qty * newItems[idx].rate;
                                  setEditData({...editData, items: newItems});
                                }}
                                className="h-6 text-[10px] w-12 px-1 text-right"
                              />
                            </TableCell>
                            <TableCell className="text-[10px] py-0.5 text-right px-1">
                              <Input
                                type="number"
                                value={item.rate}
                                onChange={(e) => {
                                  const newItems = [...editData.items];
                                  newItems[idx].rate = parseFloat(e.target.value) || 0;
                                  newItems[idx].amount = newItems[idx].qty * newItems[idx].rate;
                                  setEditData({...editData, items: newItems});
                                }}
                                className="h-6 text-[10px] w-14 px-1 text-right"
                              />
                            </TableCell>
                            <TableCell className="text-[10px] py-0.5 text-right font-semibold px-1">
                              {formatCurrency(item.qty * item.rate)}
                            </TableCell>
                            <TableCell className="text-[10px] py-0.5 px-1 text-center">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                                onClick={() => {
                                  if (editData.items.length > 1) {
                                    const newItems = editData.items.filter((_, i) => i !== idx);
                                    setEditData({...editData, items: newItems});
                                  }
                                }}
                                disabled={editData.items.length <= 1}
                                title="Remove row"
                              >
                                ×
                              </Button>
                            </TableCell>
                          </TableRow>
                        )})
                      ) : (
                        // Read-only rows
                        selectedOrder.items?.length > 0 ? (
                          selectedOrder.items.map((item, idx) => {
                            const stockQty = getStockQtyForItem(item.grade, item.sku);
                            const overrideKey = `${selectedOrder.id}_${idx}`;
                            const printGrade = printGradeOverrides[overrideKey]?.grade ?? item.grade;
                            const printSku = printGradeOverrides[overrideKey]?.sku ?? (item.sku || item.packing);
                            return (
                            <TableRow key={idx}>
                              <TableCell className="text-[10px] py-1 font-medium px-1">{idx + 1}</TableCell>
                              <TableCell className="text-[10px] py-0.5 px-0.5">
                                <Input
                                  value={printGrade}
                                  onChange={(e) => setPrintGradeOverrides(prev => ({
                                    ...prev, [overrideKey]: { ...prev[overrideKey], grade: e.target.value, sku: printSku }
                                  }))}
                                  className="h-5 text-[10px] px-1 w-16 border-dashed"
                                  title="Edit grade for print only"
                                />
                              </TableCell>
                              <TableCell className="text-[10px] py-0.5 px-0.5">
                                <Input
                                  value={printSku}
                                  onChange={(e) => setPrintGradeOverrides(prev => ({
                                    ...prev, [overrideKey]: { ...prev[overrideKey], grade: printGrade, sku: e.target.value }
                                  }))}
                                  className="h-5 text-[10px] px-1 w-16 border-dashed"
                                  title="Edit SKU for print only"
                                />
                              </TableCell>
                              <TableCell className="text-[10px] py-1 text-center px-1">
                                <span className={`font-semibold ${stockQty > 100 ? 'text-green-600' : stockQty > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                                  {stockQty !== null ? stockQty : '-'}
                                </span>
                              </TableCell>
                              <TableCell className="text-[10px] py-1 text-right px-1">{item.qty}</TableCell>
                              <TableCell className="text-[10px] py-1 text-right px-1">{formatCurrency(item.rate)}</TableCell>
                              <TableCell className="text-[10px] py-1 text-right font-semibold px-1">{formatCurrency(item.amount || item.qty * item.rate)}</TableCell>
                            </TableRow>
                          )})
                        ) : (
                          (() => {
                            const stockQty = getStockQtyForItem(selectedOrder.product_grade, selectedOrder.packing);
                            return (
                              <TableRow>
                                <TableCell className="text-[10px] py-1 font-medium px-1">1</TableCell>
                                <TableCell className="text-[10px] py-1 px-1">{selectedOrder.product_grade}</TableCell>
                                <TableCell className="text-[10px] py-1 px-1">{selectedOrder.packing}</TableCell>
                                <TableCell className="text-[10px] py-1 text-center px-1">
                                  <span className={`font-semibold ${stockQty > 100 ? 'text-green-600' : stockQty > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                                    {stockQty !== null ? stockQty : '-'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-[10px] py-1 text-right px-1">{selectedOrder.quantity_kg}</TableCell>
                                <TableCell className="text-[10px] py-1 text-right px-1">{formatCurrency(selectedOrder.unit_price)}</TableCell>
                                <TableCell className="text-[10px] py-1 text-right font-semibold px-1">{formatCurrency(selectedOrder.total_price)}</TableCell>
                              </TableRow>
                            );
                          })()
                        )
                      )}
                      {/* Total */}
                      <TableRow className="bg-amber-100 font-bold">
                        <TableCell colSpan={4} className="text-[10px] py-1.5 px-1">
                          {isEditing && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-5 text-[9px] px-2"
                              onClick={() => {
                                const newItems = [...editData.items, {
                                  grade: '',
                                  sku: 'TIN',
                                  qty: 0,
                                  rate: 0,
                                  amount: 0
                                }];
                                setEditData({...editData, items: newItems});
                              }}
                            >
                              + Add Row
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-[10px] py-1.5 text-right px-1">
                          {isEditing 
                            ? editData.items.reduce((sum, item) => sum + (item.qty || 0), 0)
                            : (selectedOrder.total_qty || selectedOrder.quantity_kg)
                          }
                        </TableCell>
                        <TableCell className="text-[10px] py-1.5 text-right px-1">TOTAL</TableCell>
                        <TableCell className="text-[10px] py-1.5 text-right text-primary px-1">
                          {isEditing
                            ? formatCurrency(editData.items.reduce((sum, item) => sum + (item.qty * item.rate || 0), 0))
                            : formatCurrency(selectedOrder.total_value || selectedOrder.total_price)
                          }
                        </TableCell>
                        {isEditing && <TableCell className="text-[10px] py-1.5 px-1"></TableCell>}
                      </TableRow>
                      <TableRow className="bg-blue-50/50 hover:bg-blue-50/50">
                        <TableCell colSpan={isEditing ? 3 : 2} className="text-[10px] py-1 text-right font-semibold text-blue-700 px-1">
                          KGs: {getOrderTotalKgs(isEditing ? { items: editData.items } : selectedOrder).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell className="text-[10px] py-1 text-right px-1"></TableCell>
                        <TableCell className="text-[10px] py-1 text-right font-bold text-blue-800 px-1">AVRG</TableCell>
                        <TableCell className="text-[10px] py-1 text-right font-bold text-blue-700 px-1">
                          {formatCurrency(Math.round(getOrderAvrg(isEditing ? { items: editData.items } : selectedOrder)))}                        </TableCell>
                        {isEditing && <TableCell className="text-[10px] py-1 px-1"></TableCell>}
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Discount Section - Only for ADV, DDAP, DDAP RECVD */}
                {['ADV', 'DDAP', 'DDAP RECVD'].includes((selectedOrder.payment_terms || '').toUpperCase()) && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
                    <p className="text-xs font-semibold text-green-800">Discount Applied (Payment: {selectedOrder.payment_terms})</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Discount/KG:</span>
                        <Input
                          type="number"
                          value={selectedOrder.discount_per_kg || 10}
                          onChange={(e) => updateDiscount(selectedOrder.id, parseFloat(e.target.value) || 10)}
                          className="h-6 text-[10px] w-20 mt-1"
                        />
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Discount:</span>
                        <p className="font-semibold text-red-600 mt-1">- {formatCurrency(selectedOrder.total_discount || 0)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Invoice Value:</span>
                        <p className="font-bold text-green-700 text-sm mt-1">{formatCurrency((selectedOrder.total_value || 0) - (selectedOrder.total_discount || 0))}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Warning for invalid product combinations in selected order */}
                {selectedOrder.items?.length > 0 && (() => {
                  const invalidOrderItems = selectedOrder.items.filter(item => 
                    item.grade && item.sku && !products.some(p => p.grade === item.grade && p.sku === item.sku)
                  );
                  return invalidOrderItems.length > 0 ? (
                    <div className="bg-amber-50 border border-amber-300 rounded-md p-2 mt-2">
                      <p className="text-amber-800 text-[10px] font-medium flex items-center gap-1">
                        <span className="text-amber-600">⚠️</span>
                        Warning: Invalid product combinations:
                      </p>
                      <p className="text-amber-700 text-[10px] mt-0.5 font-semibold">
                        {invalidOrderItems.map(i => `${i.grade}-${i.sku}`).join(', ')}
                      </p>
                    </div>
                  ) : null;
                })()}

                {/* Customer Info */}
                <div className="p-3 bg-secondary/30 rounded-lg">
                  <p className="text-xs font-semibold mb-2 flex items-center gap-1">
                    <Users className="w-3 h-3" /> Customer
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{selectedOrder.dispatch_name || selectedOrder.customer_name}</span></div>
                    <div><span className="text-muted-foreground">Phone:</span> {selectedOrder.customer_phone}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[400px]">
                <FileText className="w-16 h-16 mb-4 text-muted-foreground/30" />
                <p className="text-muted-foreground">Select an order to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </>)}

      {/* Enquiry Tab — Active Negotiations + Rejected */}
      {ordersPageTab === 'enquiry' && (() => {
        const activeEnquiries = enquiries.filter(e => ['pending', 'negotiating', 'countered'].includes(e.status));
        const allShown = activeEnquiries;
        const customerMap = {};
        allShown.forEach(req => {
          const name = req.customer_name || 'Unknown';
          if (!customerMap[name]) customerMap[name] = [];
          customerMap[name].push(req);
        });
        // Add ONLY currently-online customers who have no active requests
        Object.entries(onlineCustomers).forEach(([name, info]) => {
          if (!customerMap[name] && info.isActive) customerMap[name] = [];
        });
        const customerNames = Object.keys(customerMap).sort();

        const clearRejected = async () => {
          try {
            const res = await axios.delete(`${API}/order-requests/clear-rejected`);
            toast.success(res.data.message);
            fetchEnquiries();
          } catch (e) { toast.error("Failed to clear"); }
        };

        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="enquiry-tab">
            <Card>
              <CardContent className="p-0">
                <div className="p-2 border-b bg-secondary/30 flex items-center justify-between">
                  <p className="text-xs font-semibold">Enquiries ({customerNames.length})</p>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                  {customerNames.map(name => {
                    const reqs = customerMap[name];
                    const latestUpdate = reqs.reduce((latest, r) => {
                      const t = r.updated_at || r.created_at || '';
                      return t > latest ? t : latest;
                    }, '');
                    const istTime = latestUpdate ? new Date(new Date(latestUpdate).getTime() + 5.5*60*60*1000) : null;
                    const timeStr = istTime ? `${String(istTime.getUTCDate()).padStart(2,'0')}-${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][istTime.getUTCMonth()]}-${String(istTime.getUTCFullYear()).slice(2)} ${String(istTime.getUTCHours()).padStart(2,'0')}:${String(istTime.getUTCMinutes()).padStart(2,'0')}` : '';
                    const isOnline = !!onlineCustomers[name]?.isActive;
                    const loginInfo = onlineCustomers[name];
                    const hasLoggedInToday = !!loginInfo;
                    const loginTimeStr = loginInfo?.login_time ? (() => {
                      const d = new Date(loginInfo.login_time);
                      const ist = new Date(d.getTime() + 5.5*60*60*1000);
                      return `${String(ist.getUTCHours()).padStart(2,'0')}:${String(ist.getUTCMinutes()).padStart(2,'0')}`;
                    })() : '';
                    // Live activity label
                    const liveLabel = isOnline && loginInfo ? ({
                      login: 'Online',
                      view_orders: 'Viewing Orders',
                      view_ledger: 'Viewing Ledger',
                      view_requests: 'Viewing Requests',
                      view_new_order: 'New Order',
                      search_grade: `Searching ${loginInfo.latestDetails || ''}`.trim(),
                      edit_rate: `Editing Rate ${loginInfo.latestDetails || ''}`.trim(),
                      edit_qty: `Editing Qty ${loginInfo.latestDetails || ''}`.trim(),
                      view_rate: `Viewing Rate ${loginInfo.latestDetails || ''}`.trim(),
                      submit_request: 'Submitted Request',
                      click_freq_grade: `Freq: ${loginInfo.latestDetails || ''}`.trim(),
                      click_high_stock_grade: `High Stock: ${loginInfo.latestDetails || ''}`.trim(),
                      click_discounted_grade: `Discount: ${loginInfo.latestDetails || ''}`.trim(),
                      view_profile: 'Viewing Profile',
                      click_refresh: 'Refreshing',
                      view_dispatch: 'Viewing Dispatch',
                      view_invoice: 'Viewing Invoice',
                      delete_grade: `Deleted ${loginInfo.latestDetails || ''}`.trim(),
                    }[loginInfo.latestAction] || 'Online') : '';
                    return (
                    <button key={name}
                      onClick={() => { if (reqs.length > 0) openEnquiry(reqs[0]); }}
                      className={`w-full text-left px-3 py-2.5 border-b hover:bg-blue-50/50 transition-colors ${selectedEnquiry?.customer_name === name ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''} ${isOnline ? 'bg-green-50/40 border-l-2 border-l-green-400' : ''}`}
                      data-testid={`enquiry-customer-${name}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold truncate">{name}</span>
                        {hasLoggedInToday && (
                          <span role="button" tabIndex={0} onClick={e => { e.stopPropagation(); setLoginSessionsPopup({ name, ...loginInfo }); }}
                            className={`shrink-0 cursor-pointer hover:opacity-70 transition-opacity px-1.5 py-1 rounded-md ${isOnline ? 'hover:bg-green-100' : 'hover:bg-gray-100'}`} title="View Login Sessions"
                            data-testid={`enquiry-login-${name}`}>
                            <Badge className={`text-[9px] px-2 py-0.5 ${isOnline ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{loginTimeStr}</Badge>
                          </span>
                        )}
                        {reqs.length > 0 && <Badge variant="outline" className="text-[8px] px-1 py-0 h-4 shrink-0">{reqs.length}</Badge>}
                        {isOnline && liveLabel && (
                          <span className="text-[8px] text-green-700 font-medium truncate max-w-[120px] ml-auto animate-pulse" data-testid={`live-activity-${name}`}>
                            {liveLabel}
                          </span>
                        )}
                        {!isOnline && timeStr && <span className="text-[8px] text-muted-foreground ml-auto shrink-0">{timeStr}</span>}
                      </div>
                    </button>
                    );
                  })}
                  {customerNames.length === 0 && <p className="text-xs text-muted-foreground text-center py-8">No active negotiations</p>}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-0">
                {selectedEnquiry && customerMap[selectedEnquiry.customer_name] ? (() => {
                  const custReqs = [...customerMap[selectedEnquiry.customer_name]].sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));
                  const custId = selectedEnquiry.customer_id;
                  return (
                    <div>
                      <div className="p-3 border-b bg-secondary/30 space-y-2">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold flex-1">{selectedEnquiry.customer_name}</p>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Refresh Negotiations"
                            data-testid="refresh-enquiry"
                            onClick={() => fetchEnquiries()}>
                            <RefreshCw className="w-3.5 h-3.5 text-gray-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Customer Profile"
                            data-testid="open-customer-profile"
                            onClick={() => openBehaviourProfile(selectedEnquiry.customer_name)}>
                            <User className="w-3.5 h-3.5 text-purple-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Activity Log"
                            data-testid="open-activity-log"
                            onClick={() => setActivityLogCustomer({ id: custId, name: selectedEnquiry.customer_name })}>
                            <Clock className="w-3.5 h-3.5 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Customer Preview"
                            data-testid="open-customer-preview"
                            onClick={() => setPreviewCustomer({ id: custId, name: selectedEnquiry.customer_name, username: selectedEnquiry.customer_username })}>
                            <Eye className="w-3.5 h-3.5 text-emerald-600" />
                          </Button>
                        </div>
                        {/* Combine threads controls */}
                        {custReqs.length > 1 && (
                          <div className="flex items-center gap-2">
                            {!combineMode ? (
                              <Button size="sm" variant="outline" className="h-6 text-[9px] px-2 gap-1"
                                onClick={() => { setCombineMode(true); setCombineSelected([]); }}>
                                <Plus className="w-3 h-3" /> Combine Threads
                              </Button>
                            ) : (
                              <>
                                <span className="text-[9px] text-muted-foreground">{combineSelected.length} selected</span>
                                <Button size="sm" className="h-6 text-[9px] px-2 bg-blue-600 hover:bg-blue-700" disabled={combineSelected.length < 2}
                                  onClick={async () => {
                                    try {
                                      const res = await axios.post(`${API}/order-requests/combine-threads`, { request_ids: combineSelected });
                                      toast.success(res.data?.message || "Threads combined!");
                                      setCombineMode(false);
                                      setCombineSelected([]);
                                      fetchEnquiries();
                                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                                  }}>Combine ({combineSelected.length})</Button>
                                <Button size="sm" variant="ghost" className="h-6 text-[9px] px-2 text-red-500"
                                  onClick={() => { setCombineMode(false); setCombineSelected([]); }}>Cancel</Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="overflow-y-auto p-3 space-y-4" style={{ maxHeight: '560px' }}>
                        {custReqs.map(req => (
                          <div key={req.id} className="relative">
                            {combineMode && (
                              <label className="absolute -left-1 top-2 z-10 cursor-pointer">
                                <input type="checkbox" checked={combineSelected.includes(req.id)}
                                  onChange={e => {
                                    setCombineSelected(prev => e.target.checked ? [...prev, req.id] : prev.filter(id => id !== req.id));
                                  }}
                                  className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500" />
                              </label>
                            )}
                            <div className={combineMode ? 'ml-5' : ''}>
                              <NegotiationTable key={req.id} request={req} viewerRole="owner" onUpdate={fetchEnquiries} products={products} marketTrends={marketTrends}
                                effectiveStock={effectiveStock}
                                autoRates={custAutoRates}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })() : (
                  <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                    <MessageSquare className="w-10 h-10 mb-3 opacity-20" />
                    <p className="text-sm">Select a customer</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Confirmed Deals Tab — flat list, only deals without order_number */}
      {ordersPageTab === 'confirmed_deals' && (() => {
        const pendingConfirmed = enquiries.filter(e => ['processing', 'confirmed'].includes(e.status) && !e.order_number);

        const formatISTShort = (isoStr) => {
          if (!isoStr) return '';
          const d = new Date(isoStr);
          const ist = new Date(d.getTime() + 5.5*60*60*1000);
          return `${String(ist.getUTCDate()).padStart(2,'0')}-${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][ist.getUTCMonth()]} ${String(ist.getUTCHours()).padStart(2,'0')}:${String(ist.getUTCMinutes()).padStart(2,'0')}`;
        };

        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="confirmed-deals-tab">
            {/* Left — Flat deals list */}
            <div>
              {pendingConfirmed.length > 0 ? (
                <div className="space-y-1.5">
                  {pendingConfirmed.map(deal => {
                    const isActive = selectedDeal?.id === deal.id;
                    const totalQty = (deal.latest_items || []).reduce((s, i) => s + (i.qty || 0), 0);
                    const totalValue = (deal.latest_items || []).reduce((s, i) => s + (i.qty || 0) * (i.rate || 0), 0);
                    const actionNeg = (deal.negotiations || []).find(n => n.action === 'confirmed');
                    const confirmedBy = actionNeg?.from || '';
                    const confirmTime = actionNeg?.timestamp || deal.confirmed_at || deal.updated_at || '';
                    return (
                      <div key={deal.id}
                        onClick={() => setSelectedDeal(deal)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${isActive ? 'border-green-500 bg-green-50/50 ring-1 ring-green-300' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                        data-testid={`deal-row-${deal.id}`}>
                        <span className="text-xs font-semibold truncate" style={{maxWidth:'130px'}} title={deal.customer_name}>{deal.customer_name}</span>
                        <Badge variant="outline" className="text-[8px] px-1 py-0 shrink-0">{deal.enquiry_number}</Badge>
                        <span className="text-[10px] font-bold shrink-0">{totalQty} qty</span>
                        <span className="text-[9px] text-muted-foreground shrink-0">₹{totalValue.toLocaleString('en-IN')}</span>
                        {confirmedBy && <span className="text-[8px] text-green-600 italic shrink-0">by {confirmedBy}</span>}
                        <span className="text-[8px] text-muted-foreground ml-auto shrink-0">{formatISTShort(confirmTime)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">No pending confirmed deals</p>
                  <p className="text-xs mt-1">All confirmed deals have been converted to orders</p>
                </div>
              )}
            </div>

            {/* Right — Deal detail side panel */}
            <div className="lg:sticky lg:top-20 lg:self-start">
              {selectedDeal ? (
                <Card className="border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-bold" style={{ fontFamily: 'Manrope' }}>{selectedDeal.customer_name}</p>
                        <p className="text-[10px] text-muted-foreground">{selectedDeal.enquiry_number}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {(selectedDeal.status === 'processing' || selectedDeal.status === 'confirmed') && (
                          <Button size="sm" className="h-7 text-xs gap-1 bg-green-600 hover:bg-green-700"
                            onClick={() => openFinalizeDialog(selectedDeal)} data-testid={`create-order-side-${selectedDeal.id}`}>
                            <Plus className="w-3 h-3" /> Create Order
                          </Button>
                        )}
                        {['processing', 'confirmed', 'ordered'].includes(selectedDeal.status) && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                            title="Cancel this deal"
                            onClick={() => { setDealRejectTarget(selectedDeal); setDealRejectRemark(""); setDealRejectOpen(true); }}
                            data-testid={`cancel-deal-${selectedDeal.id}`}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <NegotiationTable request={selectedDeal} viewerRole="owner" isPreview={true} />
                  </CardContent>
                </Card>
              ) : (
                <div className="text-center py-16 text-muted-foreground bg-white border rounded-lg border-dashed">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-20" />
                  <p className="text-xs">Select a deal to view details</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}




      {/* Dispatchable Orders Tab — Full version with drag-drop, partial dispatch, outstanding */}
      {ordersPageTab === 'dispatchable' && (
        <div data-testid="dispatchable-orders-tab">
          {loadingDispatchable ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : dispatchableOrders.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                {savingPriority && <div className="text-center text-xs text-muted-foreground py-1 bg-blue-50">Saving priority...</div>}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-indigo-100">
                        <TableHead className="w-8 py-2 px-1"></TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">#</TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Order#</TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Last Drag</TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Customer</TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-right text-indigo-900">Outstanding</TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-right text-indigo-900">Qty</TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Payment</TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Dispatch</TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Short Grades</TableHead>
                        <TableHead className="font-bold py-2 px-2 text-xs text-indigo-900">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dispatchableOrders.map((o, idx) => {
                        const statusColor = o.dispatch_status === 'dispatchable' ? 'bg-green-50' : o.dispatch_status === 'partial' ? 'bg-amber-50' : o.dispatch_status === 'blocked' ? 'bg-red-50' : '';
                        const statusBadge = o.dispatch_status === 'dispatchable'
                          ? <Badge className="bg-green-100 text-green-800 text-[9px] px-1.5 py-0"><CheckCircle className="w-3 h-3 mr-0.5" />Ready</Badge>
                          : o.dispatch_status === 'partial'
                          ? <button onClick={() => dispOpenPartial(o)} className="cursor-pointer"><Badge className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0 hover:bg-amber-200"><AlertCircle className="w-3 h-3 mr-0.5" />Partial</Badge></button>
                          : o.dispatch_status === 'blocked'
                          ? <button onClick={() => dispOpenPartial(o)} className="cursor-pointer"><Badge className="bg-red-100 text-red-800 text-[9px] px-1.5 py-0 hover:bg-red-200"><AlertCircle className="w-3 h-3 mr-0.5" />Blocked</Badge></button>
                          : <Badge variant="outline" className="text-[9px] px-1.5 py-0">-</Badge>;
                        return (
                          <TableRow key={o.id}
                            draggable onDragStart={() => handleDispDragStart(idx)} onDragOver={handleDispDragOver} onDrop={(e) => handleDispDrop(e, idx)}
                            className={`${statusColor} ${dragIdx === idx ? 'opacity-50' : ''} cursor-grab active:cursor-grabbing transition-colors border-b`}
                            data-testid={`dispatchable-row-${idx}`}>
                            <TableCell className="py-1.5 px-1 text-center"><GripVertical className="w-3.5 h-3.5 text-muted-foreground/50" /></TableCell>
                            <TableCell className="py-1.5 px-2 text-xs text-muted-foreground font-mono">{idx + 1}</TableCell>
                            <TableCell className="py-1.5 px-2 text-xs font-mono font-bold">{o.order_number}</TableCell>
                            <TableCell className="py-1.5 px-2 text-xs text-muted-foreground">
                              {o.last_dragged_by ? <span title={o.last_dragged_at ? new Date(o.last_dragged_at).toLocaleString() : ''}><User className="w-3 h-3 inline mr-0.5" />{o.last_dragged_by}</span> : '-'}
                            </TableCell>
                            <TableCell className="py-1.5 px-2 text-xs truncate" style={{maxWidth:'130px'}} title={o.customer_name}>{o.customer_name}</TableCell>
                            <TableCell className="py-1.5 px-2 text-xs text-right">
                              <button onClick={() => { setDispLedgerParty(o.customer_name); setDispLedgerOpen(true); }}
                                className={`font-semibold underline decoration-dotted cursor-pointer ${o.outstanding > 0 ? 'text-red-600' : o.outstanding < 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                                {o.outstanding !== 0 ? `₹${Math.abs(Math.round(o.outstanding)).toLocaleString('en-IN')}` : '0'}{o.outstanding < 0 ? ' CR' : ''}
                              </button>
                            </TableCell>
                            <TableCell className="py-1.5 px-2 text-xs text-right font-semibold">{o.total_qty}</TableCell>
                            <TableCell className="py-1.5 px-2 text-xs">{o.payment_terms || '-'}</TableCell>
                            <TableCell className="py-1.5 px-2">{statusBadge}</TableCell>
                            <TableCell className="py-1.5 px-2 text-xs">
                              {o.short_grades?.length > 0 ? (
                                <div className="space-y-0.5">
                                  <span className="text-[9px] font-bold text-red-800 mr-1">Short: {o.short_total_qty}</span>
                                  {o.short_grades.map((s, si) => (
                                    <span key={si} className="inline-block bg-red-100 text-red-700 text-[9px] px-1.5 py-0.5 rounded mr-1">{s.grade} {s.sku} <b>-{s.short}</b></span>
                                  ))}
                                </div>
                              ) : o.dispatch_status === 'dispatchable' ? (
                                <span className="text-green-600 text-[10px]">All covered</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="py-1.5 px-2">
                              {o.status !== 'to_sort' && (
                                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] gap-1"
                                  onClick={() => dispSendToSort(o.id, o.order_number)}>
                                  <ArrowRight className="w-3 h-3" /> To Sort
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="px-3 py-2 bg-gray-50 border-t text-[10px] text-muted-foreground">
                  {dispatchableOrders.length} dispatchable orders
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-12 text-muted-foreground bg-white border rounded-lg">
              <Truck className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">No dispatchable orders</p>
            </div>
          )}
        </div>
      )}


      {/* Cancelled Orders Tab */}
      {ordersPageTab === 'cancelled' && (() => {
        const cancelledDeals = enquiries.filter(e => e.status === 'order_cancelled' || e.status === 'rejected');
        // Group by month then by day
        const monthGroups = {};
        cancelledDeals.forEach(d => {
          const dt = d.updated_at || d.created_at || '';
          const monthKey = dt.slice(0, 7);
          const dayKey = dt.slice(0, 10);
          if (!monthGroups[monthKey]) monthGroups[monthKey] = {};
          if (!monthGroups[monthKey][dayKey]) monthGroups[monthKey][dayKey] = [];
          monthGroups[monthKey][dayKey].push(d);
        });
        const sortedMonths = Object.keys(monthGroups).sort().reverse();
        const monthLabels = { '01': 'January', '02': 'February', '03': 'March', '04': 'April', '05': 'May', '06': 'June', '07': 'July', '08': 'August', '09': 'September', '10': 'October', '11': 'November', '12': 'December' };

        const reviveDeal = async (dealId, enquiryNum) => {
          try {
            await axios.put(`${API}/order-requests/${dealId}/revive`);
            toast.success(`${enquiryNum} revived to active negotiation`);
            fetchEnquiries();
          } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        };

        const formatDayLabelCancelled = (dayKey) => {
          const d = new Date(dayKey + 'T00:00:00Z');
          return `${String(d.getUTCDate()).padStart(2,'0')}-${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()]}`;
        };

        const formatISTShortCancelled = (isoStr) => {
          if (!isoStr) return '';
          const d = new Date(isoStr);
          const ist = new Date(d.getTime() + 5.5*60*60*1000);
          return `${String(ist.getUTCHours()).padStart(2,'0')}:${String(ist.getUTCMinutes()).padStart(2,'0')}`;
        };

        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="cancelled-orders-tab">
            {/* Left — Cancelled deals list */}
            <div>
              {sortedMonths.length > 0 ? sortedMonths.map(monthKey => {
                const [yr, mo] = monthKey.split('-');
                const label = `${monthLabels[mo] || mo} ${yr}`;
                const dayGroups = monthGroups[monthKey];
                const sortedDays = Object.keys(dayGroups).sort().reverse();
                const totalInMonth = sortedDays.reduce((s, d) => s + dayGroups[d].length, 0);
                return (
                  <div key={monthKey} className="mb-4">
                    <button onClick={() => {
                      const el = document.getElementById(`cancelled-${monthKey}`);
                      if (el) el.classList.toggle('hidden');
                    }} className="flex items-center gap-2 w-full p-2.5 rounded-lg bg-red-50 hover:bg-red-100 transition-colors mb-1">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-sm font-bold text-red-800">{label}</span>
                      <Badge className="bg-red-100 text-red-800 text-[10px]">{totalInMonth}</Badge>
                      <ChevronDown className="w-3.5 h-3.5 text-red-400 ml-auto" />
                    </button>
                    <div id={`cancelled-${monthKey}`} className="pl-1 space-y-2">
                      {sortedDays.map(dayKey => {
                        const deals = dayGroups[dayKey];
                        return (
                          <div key={dayKey}>
                            {(() => {
                              const custCount = new Set(deals.map(d => d.customer_name)).size;
                              const totalQtyDay = deals.reduce((s, d) => s + (d.latest_items || []).reduce((ss, i) => ss + (i.qty || 0), 0), 0);
                              const totalValueDay = deals.reduce((s, d) => s + (d.latest_items || []).reduce((ss, i) => ss + (i.qty || 0) * (i.rate || 0), 0), 0);
                              return (
                                <button onClick={() => {
                                  const el = document.getElementById(`cancelled-day-${dayKey}`);
                                  if (el) el.classList.toggle('hidden');
                                }} className="flex items-center gap-2 w-full px-2 py-1.5 text-[10px] font-bold text-red-900 bg-red-100 hover:bg-red-200 rounded transition-colors">
                                  <span className="font-extrabold">{formatDayLabelCancelled(dayKey)}</span>
                                  <Badge className="bg-red-200 text-red-900 text-[8px] px-1 py-0">{custCount} cust</Badge>
                                  <span className="text-red-800">{totalQtyDay} qty</span>
                                  <span className="text-red-800">₹{totalValueDay.toLocaleString('en-IN')}</span>
                                  <ChevronDown className="w-3 h-3 text-red-500 shrink-0 ml-auto" />
                                </button>
                              );
                            })()}
                            <div id={`cancelled-day-${dayKey}`} className="hidden space-y-1 pl-2 mt-1">
                              {deals.map(deal => {
                                const isActive = selectedDeal?.id === deal.id;
                                const totalQty = (deal.latest_items || []).reduce((s, i) => s + (i.qty || 0), 0);
                                const cancelTime = deal.updated_at || deal.created_at || '';
                                return (
                                  <div key={deal.id}
                                    onClick={() => setSelectedDeal(deal)}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer transition-colors ${isActive ? 'border-red-400 bg-red-50/60 ring-1 ring-red-300' : 'border-red-100 bg-white hover:bg-red-50/30'}`}
                                    data-testid={`cancelled-row-${deal.id}`}>
                                    <span className="text-[10px] font-mono font-bold text-red-700 shrink-0">{deal.enquiry_number}</span>
                                    <span className="text-[10px] font-semibold truncate" style={{maxWidth:'110px'}} title={deal.customer_name}>{deal.customer_name}</span>
                                    <Badge className={`text-[8px] px-1 py-0 shrink-0 ${deal.status === 'order_cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                                      {deal.status === 'order_cancelled' ? 'Cancelled' : 'Rejected'}
                                    </Badge>
                                    <span className="text-[9px] font-bold shrink-0">{totalQty} qty</span>
                                    <span className="text-[8px] text-muted-foreground ml-auto shrink-0">{formatISTShortCancelled(cancelTime)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }) : (
                <div className="text-center py-12 text-muted-foreground">
                  <XCircle className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">No cancelled orders</p>
                </div>
              )}
            </div>

            {/* Right — Cancelled deal detail side panel */}
            <div className="lg:sticky lg:top-20 lg:self-start">
              {selectedDeal && (selectedDeal.status === 'order_cancelled' || selectedDeal.status === 'rejected') ? (
                <Card className="border-red-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-bold" style={{ fontFamily: 'Manrope' }}>{selectedDeal.customer_name}</p>
                        <p className="text-[10px] text-muted-foreground">{selectedDeal.enquiry_number}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Badge className={`text-[9px] px-2 py-0.5 ${selectedDeal.status === 'order_cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                          {selectedDeal.status === 'order_cancelled' ? 'Cancelled' : 'Rejected'}
                        </Badge>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                          onClick={() => reviveDeal(selectedDeal.id, selectedDeal.enquiry_number)} data-testid={`revive-side-${selectedDeal.id}`}>
                          <RefreshCw className="w-3 h-3" /> Revive
                        </Button>
                      </div>
                    </div>
                    {selectedDeal.cancel_remark && (
                      <div className="mb-3 p-2 bg-red-50 rounded border border-red-100">
                        <p className="text-[10px] font-semibold text-red-700 mb-0.5">Cancellation Reason</p>
                        <p className="text-[11px] text-red-800">{selectedDeal.cancel_remark}</p>
                      </div>
                    )}
                    <NegotiationTable request={selectedDeal} viewerRole="owner" isPreview={true} />
                  </CardContent>
                </Card>
              ) : (
                <div className="text-center py-16 text-muted-foreground bg-white border rounded-lg border-dashed border-red-200">
                  <XCircle className="w-8 h-8 mx-auto mb-2 opacity-20 text-red-300" />
                  <p className="text-xs">Select a cancelled deal to view details</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}


      {/* Market Trends Tab */}
      {ordersPageTab === 'market_trends' && (
        <div data-testid="market-trends-tab">
          {/* Trend selection buttons */}
          <div className="flex flex-wrap gap-2 mb-3 items-center">
            <span className="text-xs font-bold text-muted-foreground mr-1">Active Trend:</span>
            {Object.entries(trendLabels).map(([key, label]) => (
              <Button key={key} size="sm"
                onClick={() => setActiveTrendOnServer(key)}
                className={`h-9 px-4 text-xs font-semibold rounded-full gap-1.5 transition-all ${
                  activeTrend === key
                    ? `${trendColors[key].bg} ${trendColors[key].hover} text-white ring-2 ring-offset-1`
                    : `bg-white border ${trendColors[key].light} hover:opacity-80`
                }`}
                data-testid={`trend-btn-${key}`}>
                {activeTrend === key && <Check className="w-3.5 h-3.5" />}
                {label}
              </Button>
            ))}
          </div>

          {/* SKU filter buttons */}
          <div className="flex flex-wrap gap-1.5 mb-3 items-center">
            <span className="text-[10px] font-bold text-muted-foreground mr-1">SKU:</span>
            <Button size="sm" variant={trendSkuFilter === 'ALL' ? 'default' : 'outline'}
              onClick={() => setTrendSkuFilter('ALL')}
              className="h-7 px-3 text-[10px] rounded-full" data-testid="sku-filter-all">ALL</Button>
            {trendSkus.map(sku => (
              <Button key={sku} size="sm" variant={trendSkuFilter === sku ? 'default' : 'outline'}
                onClick={() => setTrendSkuFilter(sku)}
                className="h-7 px-3 text-[10px] rounded-full" data-testid={`sku-filter-${sku}`}>{sku}</Button>
            ))}
          </div>

          {/* Search + Custom rate adjustment */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input value={trendSearch} onChange={e => setTrendSearch(e.target.value)}
                placeholder="Search grade..." className="pl-9 h-8 text-xs w-48"
                data-testid="trend-search" />
            </div>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-[10px] font-semibold text-muted-foreground">Adjust All Rates:</span>
              <Input
                placeholder="+200 or -200"
                title="Type a value and press Enter to adjust all rates"
                className="h-7 w-28 text-[10px] text-center font-mono"
                data-testid="bulk-adjust-input"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    const val = parseInt(e.target.value);
                    if (isNaN(val) || val === 0) { toast.error("Enter a valid +/- number"); return; }
                    await axios.put(`${API}/market-trends/bulk-adjust`, { adjustment: val, sku: trendSkuFilter !== 'ALL' ? trendSkuFilter : null });
                    toast.success(`All customer rates ${val > 0 ? '+' : ''}${val}`);
                    e.target.value = '';
                    fetchMarketTrends();
                  }
                }}
              />
            </div>
          </div>

          {/* Trends Table */}
          {loadingTrends ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold w-8">#</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold">Grade</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold">SKU</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold text-right">LTP</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">FG Stock</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold text-right bg-emerald-50 text-emerald-800">Customer Rate</TableHead>
                        <TableHead className="text-[10px] py-2 px-1.5 font-extrabold text-right bg-blue-50 text-blue-800">BSR-1</TableHead>
                        <TableHead className="text-[10px] py-2 px-1.5 font-extrabold text-right bg-indigo-50 text-indigo-800">BSR-2</TableHead>
                        <TableHead className="text-[10px] py-2 px-1.5 font-extrabold text-right bg-purple-50 text-purple-800">BSR-3</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Trades</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Demand</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {marketTrends
                        .filter(t => (trendSkuFilter === 'ALL' || t.sku === trendSkuFilter) && (!trendSearch || t.grade.toLowerCase().includes(trendSearch.toLowerCase())))
                        .sort((a, b) => {
                          // High demand grades first
                          const aDemand = (demandGrades[a.grade] || demandGrades[a.grade.toUpperCase()])?.count || 0;
                          const bDemand = (demandGrades[b.grade] || demandGrades[b.grade.toUpperCase()])?.count || 0;
                          if (bDemand !== aDemand) return bDemand - aDemand;
                          return a.grade.localeCompare(b.grade);
                        })
                        .map((t, idx) => {
                          const demand = demandGrades[t.grade] || demandGrades[t.grade.toUpperCase()];
                          const isDemand = !!demand;
                          const cr = t.customer_rate_na ? null : (t.customer_rate || 0);
                          const ltp = t.ltp || 0;
                          const rateDiffersFromLtp = cr !== null && cr > 0 && ltp > 0 && cr !== ltp;
                          const bsr1 = cr !== null && cr > 0 ? cr + 100 : null;
                          const bsr2 = bsr1 !== null ? bsr1 + 100 : null;
                          const bsr3 = bsr2 !== null ? bsr2 + 100 : null;
                          return (
                          <TableRow key={`${t.grade}-${t.sku}`}
                            className={`hover:bg-gray-50/50 ${isDemand ? 'bg-yellow-50/70 border-l-2 border-l-yellow-400' : ''}`}
                            data-testid={`trend-row-${idx}`}>
                            <TableCell className="text-[10px] py-2 px-2 text-muted-foreground">{idx + 1}</TableCell>
                            <TableCell className="py-2 px-2 text-xs font-semibold">
                              {t.grade}
                              {isDemand && <span className="ml-1 text-[8px] text-yellow-700">🔥</span>}
                            </TableCell>
                            <TableCell className="py-2 px-2 text-xs">{t.sku}</TableCell>
                            <TableCell className="py-2 px-2 text-xs text-right font-mono font-semibold">₹{ltp?.toLocaleString('en-IN') || 0}</TableCell>
                            {/* FG Stock */}
                            <TableCell className="py-2 px-2 text-xs text-center font-semibold">
                              {t.fg_stock > 0 ? t.fg_stock : <span className="text-muted-foreground">0</span>}
                            </TableCell>
                            {/* Customer Rate — editable, highlighted when different from LTP */}
                            <TableCell className={`py-1 px-1.5 text-xs text-right ${t.needs_rate ? 'bg-yellow-100 ring-1 ring-inset ring-yellow-400' : rateDiffersFromLtp ? 'bg-orange-50 ring-1 ring-inset ring-orange-300' : t.customer_rate_edited ? 'bg-emerald-50' : 'bg-gray-50/50'}`}>
                              {editingTrendPrice?.grade === t.grade && editingTrendPrice?.sku === t.sku ? (
                                <div className="flex items-center justify-end gap-1">
                                  <Input
                                    value={editingTrendPrice.price}
                                    onChange={e => setEditingTrendPrice({...editingTrendPrice, price: e.target.value})}
                                    className="h-6 w-20 text-[10px] text-right p-1"
                                    placeholder="Rate or NA"
                                    autoFocus onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        const val = editingTrendPrice.price;
                                        const isNA = String(val).toUpperCase().trim() === 'NA';
                                        axios.put(`${API}/market-trends/override`, { grade: t.grade, sku: t.sku, trend_type: "customer_rate", price: isNA ? 0 : parseFloat(val) || 0, is_na: isNA })
                                          .then(() => { toast.success(`Rate updated for ${t.grade}`); setEditingTrendPrice(null); fetchMarketTrends(); })
                                          .catch(() => toast.error("Failed"));
                                      }
                                    }}
                                  />
                                  <button onClick={() => {
                                    const val = editingTrendPrice.price;
                                    const isNA = String(val).toUpperCase().trim() === 'NA';
                                    axios.put(`${API}/market-trends/override`, { grade: t.grade, sku: t.sku, trend_type: "customer_rate", price: isNA ? 0 : parseFloat(val) || 0, is_na: isNA })
                                      .then(() => { toast.success(`Rate updated`); setEditingTrendPrice(null); fetchMarketTrends(); })
                                      .catch(() => toast.error("Failed"));
                                  }} className="text-green-600 hover:text-green-800"><Check className="w-3 h-3" /></button>
                                  <button onClick={() => setEditingTrendPrice(null)} className="text-red-400"><X className="w-3 h-3" /></button>
                                </div>
                              ) : (
                                <button onClick={() => setEditingTrendPrice({ grade: t.grade, sku: t.sku, price: t.customer_rate_na ? 'NA' : (t.customer_rate || t.ltp || '') })}
                                  className={`text-right w-full rounded px-1 py-0.5 transition-colors hover:bg-emerald-100/50 font-mono ${t.customer_rate_na ? 'font-bold text-red-600' : rateDiffersFromLtp ? 'font-bold text-orange-700' : t.customer_rate_edited ? 'font-bold text-emerald-800' : 'font-semibold text-gray-700'}`}>
                                  {t.customer_rate_na ? 'NA' : cr > 0 ? `₹${cr.toLocaleString('en-IN')}` : <span className="text-[9px] text-muted-foreground italic">{t.needs_rate ? 'Enter rate' : '-'}</span>}
                                </button>
                              )}
                            </TableCell>
                            {/* BSR-1 = Customer Rate + 100 */}
                            <TableCell className="py-2 px-1.5 text-[10px] text-right font-mono font-semibold bg-blue-50/40 text-blue-800">
                              {bsr1 !== null ? `₹${bsr1.toLocaleString('en-IN')}` : '-'}
                            </TableCell>
                            {/* BSR-2 = BSR-1 + 100 */}
                            <TableCell className="py-2 px-1.5 text-[10px] text-right font-mono font-semibold bg-indigo-50/40 text-indigo-800">
                              {bsr2 !== null ? `₹${bsr2.toLocaleString('en-IN')}` : '-'}
                            </TableCell>
                            {/* BSR-3 = BSR-2 + 100 */}
                            <TableCell className="py-2 px-1.5 text-[10px] text-right font-mono font-semibold bg-purple-50/40 text-purple-800">
                              {bsr3 !== null ? `₹${bsr3.toLocaleString('en-IN')}` : '-'}
                            </TableCell>
                            <TableCell className="py-2 px-2 text-xs text-center">
                              <button onClick={() => setTrendDetailPopup({ type: 'trades', grade: t.grade, sku: t.sku, data: t.recent_trades || [], count: t.trade_count, ltp: t.ltp })}
                                className="hover:opacity-70 transition-opacity">
                                <Badge variant="outline" className="text-[9px] cursor-pointer hover:bg-gray-100">{t.trade_count}</Badge>
                              </button>
                            </TableCell>
                            <TableCell className="py-2 px-2 text-xs text-center">
                              {isDemand ? (
                                <button onClick={() => setTrendDetailPopup({ type: 'demand', grade: t.grade, sku: t.sku, data: demand })}
                                  className="hover:opacity-70 transition-opacity">
                                  <Badge className="bg-yellow-100 text-yellow-800 text-[9px] cursor-pointer hover:bg-yellow-200">
                                    {demand.count}x
                                  </Badge>
                                </button>
                              ) : ''}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="px-3 py-2 bg-gray-50 border-t text-[10px] text-muted-foreground flex items-center gap-2">
                  <TrendingUp className="w-3 h-3" />
                  <span>{marketTrends.filter(t => trendSkuFilter === 'ALL' || t.sku === trendSkuFilter).length} grades</span>
                  <span className="mx-1">|</span>
                  <span>Active: <strong className={trendColors[activeTrend]?.light}>{trendLabels[activeTrend]}</strong></span>
                  {Object.keys(demandGrades).length > 0 && (
                    <>
                      <span className="mx-1">|</span>
                      <span>🔥 {Object.keys(demandGrades).length} in demand</span>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}


      {/* Trade/Demand Detail Popup */}
      <Dialog open={!!trendDetailPopup} onOpenChange={(open) => { if (!open) setTrendDetailPopup(null); }}>
        <DialogContent className="sm:max-w-[440px] max-h-[80vh] overflow-y-auto" data-testid="trend-detail-popup">
          {trendDetailPopup?.type === 'trades' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sm font-bold" style={{ fontFamily: 'Manrope' }}>
                  Trade History — {trendDetailPopup.grade} {trendDetailPopup.sku}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {trendDetailPopup.count} total trades | LTP: ₹{trendDetailPopup.ltp?.toLocaleString('en-IN')}
                </DialogDescription>
              </DialogHeader>
              <div className="border rounded-lg overflow-hidden mt-2">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold w-8">#</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">Customer</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-right">Rate</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(trendDetailPopup.data || []).map((trade, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-[10px] py-1.5 px-2 text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="text-xs py-1.5 px-2 font-medium">{trade.customer}</TableCell>
                        <TableCell className="text-xs py-1.5 px-2 text-right font-mono font-semibold">₹{trade.rate?.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-[10px] py-1.5 px-2 text-muted-foreground">{trade.date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {trendDetailPopup.count > 10 && (
                <p className="text-[10px] text-muted-foreground mt-1 text-center">Showing latest 10 of {trendDetailPopup.count} trades</p>
              )}
            </>
          )}
          {trendDetailPopup?.type === 'demand' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sm font-bold" style={{ fontFamily: 'Manrope' }}>
                  Customer Demand — {trendDetailPopup.grade}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {trendDetailPopup.data?.count || 0} searches in the last 7 days
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2 space-y-2">
                <p className="text-[10px] font-bold text-muted-foreground">Customers searching this grade:</p>
                <div className="flex flex-wrap gap-2">
                  {(trendDetailPopup.data?.customers || []).map((cust, i) => (
                    <Badge key={i} className="bg-yellow-100 text-yellow-800 text-xs px-3 py-1">{cust}</Badge>
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                  <p className="text-xs text-amber-800 font-semibold">Follow-up Suggestion</p>
                  <p className="text-[10px] text-amber-700 mt-1">
                    {trendDetailPopup.data?.count >= 3
                      ? `High demand! ${trendDetailPopup.data?.customers?.length} customer(s) actively looking for ${trendDetailPopup.grade}. Consider reaching out with competitive pricing.`
                      : `${trendDetailPopup.data?.customers?.length} customer(s) showed interest in ${trendDetailPopup.grade}. Monitor for repeat searches.`
                    }
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>



      {/* Create New Order Dialog — rendered at top level so it works from any tab */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) { resetForm(); setLinkedDealId(null); setLinkedDealEnquiry(""); }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-4" data-testid="create-order-dialog">
          <DialogHeader className="pb-2 border-b">
            <DialogTitle className="text-base font-semibold flex items-center gap-3">
              {linkedDealId ? 'Create Order from Deal' : 'Create New Order'}
              {linkedDealEnquiry && (
                <Badge variant="outline" className="text-xs font-mono">{linkedDealEnquiry}</Badge>
              )}
              {nextOrderNumber && (
                <Badge className="bg-green-600 text-white text-sm px-3 py-1">
                  {nextOrderNumber}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 pt-3">
            {/* Row 1: Order Date + AVRG */}
            <div className="flex items-center gap-3">
              <Label className="text-xs font-semibold text-gray-700 w-20">Order Date</Label>
              <DateInputDDMonYY
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                className="h-8 w-40"
              />
              {totalKgs > 0 && (
                <div className="flex items-center gap-3 ml-auto bg-blue-50 border border-blue-200 rounded px-3 py-1">
                  <span className="text-[10px] text-muted-foreground">KGs: <strong className="text-foreground">{totalKgs.toLocaleString('en-IN')}</strong></span>
                  <span className="text-[10px] text-muted-foreground">Value: <strong className="text-green-700">{formatCurrency(totalValue)}</strong></span>
                  <span className="text-xs font-bold text-blue-700">AVRG: {formatCurrency(Math.round(formAvrg))}</span>
                </div>
              )}
            </div>

            {/* Row 2: Customer Search + Bill Type + Payment Terms */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search customer..."
                    value={selectedCustomer ? selectedCustomer.name : customerSearch}
                    onChange={(e) => {
                      if (selectedCustomer) setSelectedCustomer(null);
                      setCustomerSearch(e.target.value);
                      setCustomerFocusIndex(0);
                    }}
                    onKeyDown={handleCustomerKeyDown}
                    className="pl-8 h-8 text-sm"
                  />
                  {selectedCustomer && (
                    <Button variant="ghost" size="sm" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 text-red-500" onClick={() => { setSelectedCustomer(null); setCustomerSearch(""); }}>
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                {/* Bill Type */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-amber-700 font-medium">Bill:</span>
                  <Button type="button" variant={estType === "EST" ? "default" : "outline"} size="sm" className={`h-7 px-2 text-xs ${estType === "EST" ? "bg-amber-600" : ""}`} onClick={() => setEstType("EST")}>EST</Button>
                  <Button type="button" variant={estType === "INV" ? "default" : "outline"} size="sm" className={`h-7 px-2 text-xs ${estType === "INV" ? "bg-amber-600" : ""}`} onClick={() => setEstType("INV")}>INV</Button>
                </div>
                {/* Payment Terms */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-purple-700 font-medium">Pay:</span>
                  {paymentOptions.map((opt) => (
                    <Button key={opt} type="button" variant={paymentTerms === opt ? "default" : "outline"} size="sm" className={`h-7 px-2 text-xs ${paymentTerms === opt ? "bg-purple-600" : ""}`} onClick={() => setPaymentTerms(opt)}>{opt}</Button>
                  ))}
                </div>
              </div>
              {/* Customer Search Results */}
              {customerSearch && filteredCustomers.length > 0 && !selectedCustomer && (
                <div className="mt-2 max-h-32 overflow-y-auto border rounded-md bg-white text-xs">
                  {filteredCustomers.slice(0, 8).map((c, idx) => (
                    <div key={c.id} className={`px-2 py-1.5 cursor-pointer border-b last:border-b-0 ${idx === customerFocusIndex ? 'bg-blue-100' : 'hover:bg-blue-50'}`}
                      onClick={() => { setSelectedCustomer(c); setCustomerSearch(""); if (c.default_payment_terms) setPaymentTerms(c.default_payment_terms); }}>
                      <span className="font-medium">{c.name}</span>
                      {c.company && <span className="text-muted-foreground ml-1">({c.company})</span>}
                      {c.city && <span className="text-muted-foreground ml-1">• {c.city}</span>}
                    </div>
                  ))}
                  <p className="text-[10px] text-center py-1 bg-gray-50 text-muted-foreground">↑↓ Navigate, Enter Select</p>
                </div>
              )}
              {/* Customer Defaults */}
              {selectedCustomer && (
                <div className="mt-2 flex items-center gap-4 text-xs bg-green-100 px-2 py-1.5 rounded border border-green-300">
                  <span className="text-green-800 font-medium">Defaults:</span>
                  <span><b>Bill:</b> {selectedCustomer.buyer_type || 'N/A'}</span>
                  <span><b>Pay:</b> {selectedCustomer.default_payment_terms || 'N/A'}</span>
                  <span><b>City:</b> {selectedCustomer.city || 'N/A'}</span>
                </div>
              )}
            </div>

            {/* Stock Info Banner */}
            <div className="px-2 py-1.5 bg-cyan-50 border border-cyan-200 rounded text-xs text-cyan-800">
              <b>Stock:</b> {orderItems.filter(i => i.grade && i.sku).map((item, idx) => {
                const stock = getStockQty(item.grade, item.sku);
                if (stock === null) return null;
                return <span key={idx} className={`inline-block mx-1 px-1.5 py-0.5 rounded ${stock > 100 ? 'bg-green-100 text-green-700' : stock > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{item.grade}-{item.sku}: {stock}</span>;
              })}
              {!orderItems.some(i => i.grade && i.sku) && <span className="text-muted-foreground italic">Select grade & packing to see stock</span>}
            </div>

            {/* Order Items Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-green-600 hover:bg-green-600">
                    <TableHead className="text-[10px] py-1.5 font-bold text-white w-6 px-1 text-center">#</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-1 text-center w-28">Grade</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-1 text-center w-24">Packing</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-1 text-center w-14">Stock</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-1 text-center w-16">Qty</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-1 text-center w-20">Rate</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-1 text-center w-20">Amount</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white w-8 px-0"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orderItems.map((item, idx) => {
                    const searchTerm = (item.gradeSearch || '').toLowerCase();
                    const filteredGrades = uniqueGrades.filter(g => g.toLowerCase().includes(searchTerm))
                      .sort((a, b) => {
                        const aLower = a.toLowerCase();
                        const bLower = b.toLowerCase();
                        if (aLower === searchTerm && bLower !== searchTerm) return -1;
                        if (bLower === searchTerm && aLower !== searchTerm) return 1;
                        const aStarts = aLower.startsWith(searchTerm);
                        const bStarts = bLower.startsWith(searchTerm);
                        if (aStarts && !bStarts) return -1;
                        if (bStarts && !aStarts) return 1;
                        return a.length - b.length;
                      }).slice(0, 12);
                    const filteredSkus = skuTypes.filter(s => s.toLowerCase().includes((item.skuSearch || '').toLowerCase()));
                    const stockQty = getStockQty(item.grade, item.sku);
                    return (
                    <TableRow key={idx} className="hover:bg-gray-50">
                      <TableCell className="py-1 text-[10px] font-medium px-1 text-center text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="py-1 px-0.5 relative">
                        <Input placeholder="Grade..." value={item.gradeSearch || item.grade} onChange={(e) => { updateItemRow(idx, 'gradeSearch', e.target.value); updateItemRow(idx, 'gradeFocusIdx', 0); if (!e.target.value) updateItemRow(idx, 'grade', ''); }} onKeyDown={(e) => handleGradeKeyDown(e, idx, filteredGrades)} className="h-7 text-[11px] w-full px-1" />
                        {item.gradeSearch && !item.grade && filteredGrades.length > 0 && (
                          <div className="absolute z-10 mt-1 left-0.5 right-0.5 max-h-28 overflow-y-auto border rounded bg-white shadow-lg text-[11px]">
                            {filteredGrades.map((g, gIdx) => (<div key={g} className={`px-1.5 py-1 cursor-pointer border-b last:border-b-0 ${gIdx === (item.gradeFocusIdx || 0) ? 'bg-green-100' : 'hover:bg-green-50'}`} onClick={() => { updateItemRow(idx, 'grade', g); updateItemRow(idx, 'gradeSearch', g); }}>{g}</div>))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-1 px-0.5 relative">
                        <Input placeholder="Pack..." value={item.skuSearch || item.sku} onChange={(e) => { updateItemRow(idx, 'skuSearch', e.target.value); updateItemRow(idx, 'skuFocusIdx', 0); if (!e.target.value) updateItemRow(idx, 'sku', ''); }} onKeyDown={(e) => handleSkuKeyDown(e, idx, filteredSkus)} className="h-7 text-[11px] w-full px-1" />
                        {item.skuSearch && !item.sku && filteredSkus.length > 0 && (
                          <div className="absolute z-10 mt-1 left-0.5 right-0.5 max-h-28 overflow-y-auto border rounded bg-white shadow-lg text-[11px]">
                            {filteredSkus.map((s, sIdx) => (<div key={s} className={`px-1.5 py-1 cursor-pointer border-b last:border-b-0 ${sIdx === (item.skuFocusIdx || 0) ? 'bg-green-100' : 'hover:bg-green-50'}`} onClick={() => { updateItemRow(idx, 'sku', s); updateItemRow(idx, 'skuSearch', s); }}>{s}</div>))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-1 text-center text-[11px] px-0.5">
                        {stockQty !== null ? <span className={`font-semibold ${stockQty > 100 ? 'text-green-600' : stockQty > 0 ? 'text-amber-600' : 'text-red-600'}`}>{stockQty}</span> : '-'}
                      </TableCell>
                      <TableCell className="py-1 px-0.5">
                        <Input type="text" inputMode="numeric" value={item.qty} onChange={(e) => updateItemRow(idx, 'qty', e.target.value.replace(/[^0-9]/g, ''))} className="h-7 text-[11px] w-full px-1 text-center" placeholder="0" />
                      </TableCell>
                      <TableCell className="py-1 px-0.5">
                        <Input type="text" inputMode="numeric" value={item.rate} onChange={(e) => updateItemRow(idx, 'rate', e.target.value.replace(/[^0-9]/g, ''))} className="h-7 text-[11px] w-full px-1 text-center" placeholder="0" />
                      </TableCell>
                      <TableCell className="py-1 text-center text-[11px] font-semibold px-0.5 text-green-700">
                        {item.qty && item.rate ? formatCurrency(parseFloat(item.qty) * parseFloat(item.rate)) : '-'}
                      </TableCell>
                      <TableCell className="py-1 px-0">
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-700" onClick={() => deleteItemRow(idx)}><Trash2 className="w-3 h-3" /></Button>
                      </TableCell>
                    </TableRow>
                  )})}
                  {/* Total Row */}
                  <TableRow className="bg-amber-100 font-bold hover:bg-amber-100">
                    <TableCell colSpan={4} className="py-1.5 px-1">
                      <Button variant="outline" size="sm" onClick={addMoreRows} className="h-6 text-[10px] px-2 bg-white">+ Add Rows</Button>
                    </TableCell>
                    <TableCell className="py-1.5 text-xs text-center font-bold">{totalQty || 0}</TableCell>
                    <TableCell className="py-1.5 text-[10px] text-center font-semibold text-muted-foreground">TOTAL</TableCell>
                    <TableCell className="py-1.5 text-xs text-center font-bold text-green-700">{formatCurrency(totalValue)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  {totalKgs > 0 && (
                  <TableRow className="bg-blue-50 hover:bg-blue-50">
                    <TableCell colSpan={4} className="py-1 text-[10px] text-right font-semibold text-blue-700">KGs: {totalKgs.toLocaleString('en-IN')}</TableCell>
                    <TableCell colSpan={2} className="py-1 text-[10px] text-center font-bold text-blue-800">AVRG</TableCell>
                    <TableCell className="py-1 text-xs text-center font-bold text-blue-700">{formatCurrency(Math.round(formAvrg))}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Remark */}
            <div className="flex items-start gap-2">
              <Label className="text-xs font-semibold text-gray-600 w-16 pt-2">Remark</Label>
              <Textarea placeholder="Enter remarks..." value={orderRemark} onChange={(e) => setOrderRemark(e.target.value)} className="text-xs min-h-[40px] resize-none flex-1" />
            </div>

            {/* Warning */}
            {invalidItems.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs">
                <span className="text-amber-600">⚠️</span> <span className="text-amber-800 font-medium">Invalid combinations:</span> <span className="text-amber-700">{invalidItems.join(', ')}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreateOrder} className="px-4 bg-green-600 hover:bg-green-700">Create Order</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finalize Deal → Create Order Dialog */}
      <Dialog open={finalizeDialogOpen} onOpenChange={(open) => {
        setFinalizeDialogOpen(open);
        if (!open) { setFinalizeDeal(null); setFinalizeItems([]); }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-4" data-testid="finalize-order-dialog">
          <DialogHeader className="pb-2 border-b">
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-green-600" />
              Create Order from Deal
              {finalizeDeal?.enquiry_number && (
                <Badge variant="outline" className="text-xs font-mono">{finalizeDeal.enquiry_number}</Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {finalizeDeal?.customer_name} — Review and modify items, then create the final order.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-3">
            {/* Customer & Payment — same as main order form */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label className="text-[10px] font-semibold text-muted-foreground">Customer</Label>
                  <p className="text-sm font-semibold">{finalizeDeal?.customer_name}</p>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-muted-foreground">Dispatch Name</Label>
                  <Input value={finalizeDispatchName} onChange={e => setFinalizeDispatchName(e.target.value)}
                    className="h-8 text-sm mt-0.5" data-testid="finalize-dispatch-name" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-muted-foreground">Estimate Type</Label>
                  <Select value={finalizeEstimateType} onValueChange={setFinalizeEstimateType}>
                    <SelectTrigger className="h-8 text-xs mt-0.5"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TAX">TAX</SelectItem>
                      <SelectItem value="NON-TAX">NON-TAX</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-muted-foreground">Created By</Label>
                  <p className="text-sm font-medium text-blue-700 mt-1">{(() => { try { return JSON.parse(localStorage.getItem('user'))?.username || 'admin'; } catch { return 'admin'; } })()}</p>
                </div>
              </div>
              <div className="mt-2">
                <Label className="text-[10px] font-semibold text-muted-foreground">Payment Terms</Label>
                <div className="flex gap-1 mt-0.5 flex-wrap">
                  {paymentOptions.map(opt => (
                    <Button key={opt} type="button" variant={finalizePayTerms === opt ? "default" : "outline"} size="sm"
                      className={`h-7 px-2 text-xs ${finalizePayTerms === opt ? "bg-purple-600" : ""}`}
                      onClick={() => setFinalizePayTerms(opt)}>{opt}</Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Discount section for ADV/DDAP */}
            {['ADV', 'DDAP', 'DDAP RECVD'].includes(finalizePayTerms?.toUpperCase()) && (
              <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <Label className="text-[10px] font-semibold text-red-700">Discount ₹/kg</Label>
                <Input type="number" value={finalizeDiscountPerKg}
                  onChange={e => setFinalizeDiscountPerKg(parseFloat(e.target.value) || 0)}
                  className="h-7 w-20 text-xs text-center" />
                <span className="text-xs text-red-700 font-semibold">
                  Total: -₹{(() => {
                    let d = 0;
                    for (const item of finalizeItems) {
                      const pkgMap = { 'BUCKET': 10, 'TIN': 20, 'TIN_REJ': 20, 'VACUUM_10': 10, 'VACUUM_20': 20 };
                      d += (finalizeDiscountPerKg || 0) * (pkgMap[item.sku] || 10) * (parseInt(item.qty) || 0);
                    }
                    return d.toLocaleString('en-IN');
                  })()}
                </span>
              </div>
            )}

            {/* Items Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-green-600 hover:bg-green-600">
                    <TableHead className="text-[10px] py-1.5 font-bold text-white w-6 px-1 text-center">#</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-2">Grade</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-2">SKU</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-2 text-center w-20">Qty</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-2 text-center w-24">Rate (₹)</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white px-2 text-right w-24">Amount</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-white w-8 px-0"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {finalizeItems.map((item, idx) => (
                    <TableRow key={idx} data-testid={`finalize-item-${idx}`}>
                      <TableCell className="py-1 text-[10px] font-medium px-1 text-center text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="py-1 px-2 text-xs font-semibold">{item.grade}</TableCell>
                      <TableCell className="py-1 px-2 text-xs">{item.sku}</TableCell>
                      <TableCell className="py-1 px-1">
                        <Input type="number" value={item.qty}
                          onChange={e => setFinalizeItems(prev => prev.map((it, i) => i === idx ? { ...it, qty: e.target.value } : it))}
                          className="h-7 text-xs text-center w-16 mx-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid={`finalize-qty-${idx}`} />
                      </TableCell>
                      <TableCell className="py-1 px-1">
                        <Input type="number" value={item.rate}
                          onChange={e => setFinalizeItems(prev => prev.map((it, i) => i === idx ? { ...it, rate: e.target.value } : it))}
                          className="h-7 text-xs text-center w-20 mx-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid={`finalize-rate-${idx}`} />
                      </TableCell>
                      <TableCell className="py-1 px-2 text-xs text-right font-semibold text-green-700">
                        {(parseInt(item.qty) || 0) * (parseFloat(item.rate) || 0) > 0
                          ? `₹${((parseInt(item.qty) || 0) * (parseFloat(item.rate) || 0)).toLocaleString('en-IN')}`
                          : '-'}
                      </TableCell>
                      <TableCell className="py-1 px-0">
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-700"
                          onClick={() => setFinalizeItems(prev => prev.filter((_, i) => i !== idx))}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total */}
                  <TableRow className="bg-amber-100 font-bold hover:bg-amber-100">
                    <TableCell colSpan={3} className="py-1.5 px-2 text-xs text-right font-semibold">TOTAL</TableCell>
                    <TableCell className="py-1.5 text-xs text-center font-bold">
                      {finalizeItems.reduce((s, i) => s + (parseInt(i.qty) || 0), 0)}
                    </TableCell>
                    <TableCell className="py-1.5"></TableCell>
                    <TableCell className="py-1.5 text-xs text-right font-bold text-green-700">
                      ₹{finalizeItems.reduce((s, i) => s + ((parseInt(i.qty) || 0) * (parseFloat(i.rate) || 0)), 0).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Notes */}
            <div className="flex items-start gap-2">
              <Label className="text-xs font-semibold text-gray-600 w-16 pt-2">Notes</Label>
              <Textarea placeholder="Order notes..." value={finalizeNotes} onChange={e => setFinalizeNotes(e.target.value)}
                className="text-xs min-h-[40px] resize-none flex-1" data-testid="finalize-notes" />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => setFinalizeDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleFinalizeOrder} disabled={finalizing}
                className="px-4 bg-green-600 hover:bg-green-700 gap-1" data-testid="finalize-create-order-btn">
                {finalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Create Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete order {orderToDelete?.order_number}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dispatch Confirmation Dialog */}
      <AlertDialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-600" />
              Dispatch Order
            </AlertDialogTitle>
            <AlertDialogDescription>
              Move order <strong>{selectedOrder?.order_number}</strong> to Warehouse - To Sort?
              <div className="mt-2 p-2 bg-secondary/50 rounded text-sm">
                <div><strong>Customer:</strong> {selectedOrder?.dispatch_name || selectedOrder?.customer_name}</div>
                <div><strong>Total Qty:</strong> {selectedOrder?.total_qty || selectedOrder?.quantity_kg}</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={dispatching}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDispatch} 
              disabled={dispatching}
              className="bg-green-600 hover:bg-green-700"
            >
              {dispatching ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Dispatching...</> : <><Truck className="w-4 h-4 mr-1" /> Dispatch</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Partial Dispatch Dialog */}
      <Dialog open={partialDispatchDialogOpen} onOpenChange={setPartialDispatchDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Partial Dispatch - {selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter the quantity to dispatch for each item. Remaining quantity will stay in the order.
            </p>
            
            {/* Items Table for Partial Dispatch */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-100">
                    <TableHead className="text-[10px] py-1.5 font-bold px-2">GRADE</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold px-2">TYPE</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-center px-2">TOTAL</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-center px-2">DISPATCH</TableHead>
                    <TableHead className="text-[10px] py-1.5 font-bold text-center px-2">REMAINING</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dispatchItems.map((item, idx) => (
                    <TableRow key={`${item.grade}-${item.sku}-${idx}`}>
                      <TableCell className="text-xs py-1.5 px-2 font-semibold">{item.grade}</TableCell>
                      <TableCell className="text-xs py-1.5 px-2">{item.sku}</TableCell>
                      <TableCell className="text-xs py-1.5 text-center px-2 font-medium">{item.qty}</TableCell>
                      <TableCell className="text-xs py-1.5 px-2">
                        <Input
                          type="number"
                          min="0"
                          max={item.qty}
                          value={item.dispatch_qty}
                          onChange={(e) => updateDispatchQty(idx, e.target.value)}
                          className="h-7 text-xs text-center w-16 mx-auto"
                        />
                      </TableCell>
                      <TableCell className="text-xs py-1.5 text-center px-2">
                        <span className={`font-semibold ${item.remaining_qty > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {item.remaining_qty}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Summary */}
            <div className="p-2 bg-secondary/50 rounded-lg text-sm space-y-1">
              <div className="flex justify-between">
                <span>Total to Dispatch:</span>
                <span className="font-bold text-green-600">{dispatchItems.reduce((sum, i) => sum + (i.dispatch_qty || 0), 0)} qty</span>
              </div>
              <div className="flex justify-between">
                <span>Remaining in Order:</span>
                <span className="font-bold text-amber-600">{dispatchItems.reduce((sum, i) => sum + (i.remaining_qty || 0), 0)} qty</span>
              </div>
              {(() => {
                const dispatchValue = dispatchItems.reduce((sum, i) => sum + ((i.dispatch_qty || 0) * (i.rate || 0)), 0);
                const payTerms = (selectedOrder?.payment_terms || '').toUpperCase();
                const discountTerms = ['ADV', 'DDAP', 'DDAP RECVD'];
                const hasDiscount = discountTerms.includes(payTerms);
                const discountPerKg = hasDiscount ? (selectedOrder?.discount_per_kg || 10) : 0;
                let discountAmt = 0;
                if (hasDiscount || discountPerKg > 0) {
                  dispatchItems.forEach(item => {
                    const pkgKg = getPackingKg(item);
                    discountAmt += discountPerKg * pkgKg * (item.dispatch_qty || 0);
                  });
                }
                const netValue = dispatchValue - discountAmt;
                return (
                  <>
                    <div className="flex justify-between border-t pt-1 mt-1">
                      <span>Dispatch Value:</span>
                      <span className="font-bold">{formatCurrency(dispatchValue)}</span>
                    </div>
                    {(hasDiscount || discountAmt > 0) && (
                      <div className="flex justify-between text-red-600">
                        <span>Discount ({payTerms} @ ₹{discountPerKg}/kg):</span>
                        <span className="font-bold">- {formatCurrency(discountAmt)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-green-700 border-t pt-1">
                      <span>Net Value:</span>
                      <span>{formatCurrency(netValue)}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPartialDispatchDialogOpen(false)} disabled={dispatching}>
                Cancel
              </Button>
              <Button onClick={handlePartialDispatch} disabled={dispatching} className="bg-blue-600 hover:bg-blue-700">
                {dispatching ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Processing...</> : <><Truck className="w-4 h-4 mr-1" /> Dispatch Selected</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Repost to Google Sheets Dialog */}
      <Dialog open={repostDialogOpen} onOpenChange={setRepostDialogOpen}>
        <DialogContent className="max-w-md" data-testid="repost-sheets-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Post to Google Sheet?
            </DialogTitle>
            <DialogDescription>
              Order <strong>{repostingOrder?.order_number}</strong> has been updated. 
              Post the updated data to Google Sheet to keep it in sync.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-3 bg-secondary/30 rounded-lg text-sm space-y-1">
            <p><strong>Order:</strong> {repostingOrder?.order_number}</p>
            <p><strong>Customer:</strong> {repostingOrder?.dispatch_name || repostingOrder?.customer_name}</p>
            <p><strong>Payment:</strong> {repostingOrder?.payment_terms || '-'}</p>
            {repostingOrder?.status === 'dispatched' && (
              <p className="text-green-600 mt-1">
                <Truck className="w-3 h-3 inline mr-1" />
                Dispatched — will also update DISPATCH QTY sheet
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setRepostDialogOpen(false); setRepostingOrder(null); }}>
              Skip
            </Button>
            <Button onClick={repostToGoogleSheet} disabled={reposting} className="bg-green-600 hover:bg-green-700">
              {reposting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Posting...</> : <><FileSpreadsheet className="w-4 h-4 mr-1" /> Post to Sheet</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Tracking Dialog */}
      <Dialog open={showTracking} onOpenChange={setShowTracking}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto p-4">
          <DialogHeader className="pb-2 border-b">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5" /> Order Tracking
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {/* View Mode + Date Selection */}
            <div className="flex items-center gap-2 flex-wrap">
              {['day', 'week', 'month'].map(v => (
                <Button key={v} size="sm" variant={trackingView === v ? 'default' : 'outline'} onClick={() => setTrackingView(v)} className="h-7 text-xs capitalize">
                  {v === 'day' ? 'Day' : v === 'week' ? 'Week' : 'Month'}
                </Button>
              ))}
              <div className="flex items-center gap-1 ml-2">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => {
                  const d = new Date(trackingDate);
                  if (trackingView === 'day') d.setDate(d.getDate() - 1);
                  else if (trackingView === 'week') d.setDate(d.getDate() - 7);
                  else d.setMonth(d.getMonth() - 1);
                  setTrackingDate(d);
                }}>&lt;</Button>
                <span className="text-sm font-medium min-w-[140px] text-center">
                  {trackingView === 'day' && format(trackingDate, 'dd MMM yyyy')}
                  {trackingView === 'week' && `${format(startOfWeek(trackingDate, {weekStartsOn:1}), 'dd MMM')} - ${format(endOfWeek(trackingDate, {weekStartsOn:1}), 'dd MMM yyyy')}`}
                  {trackingView === 'month' && format(trackingDate, 'MMMM yyyy')}
                </span>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => {
                  const d = new Date(trackingDate);
                  if (trackingView === 'day') d.setDate(d.getDate() + 1);
                  else if (trackingView === 'week') d.setDate(d.getDate() + 7);
                  else d.setMonth(d.getMonth() + 1);
                  setTrackingDate(d);
                }}>&gt;</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs ml-1" onClick={() => setTrackingDate(new Date())}>Today</Button>
              </div>
              <Badge variant="outline" className="ml-auto text-xs">{getTrackingFiltered().length} orders</Badge>
            </div>

            {/* Tracking Table */}
            {trackingLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <div className="border rounded overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="text-xs w-[70px]">Order #</TableHead>
                      <TableHead className="text-xs">Customer</TableHead>
                      <TableHead className="text-xs text-right w-[45px]">Qty</TableHead>
                      <TableHead className="text-xs w-[50px]">Status</TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><Package className="w-3 h-3 mb-0.5" /><span>To Sort</span></div></TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><Clock className="w-3 h-3 mb-0.5" /><span>Preparing</span></div></TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><Check className="w-3 h-3 mb-0.5" /><span>Ready</span></div></TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><ClipboardList className="w-3 h-3 mb-0.5" /><span>Sequence</span></div></TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><Truck className="w-3 h-3 mb-0.5" /><span>Dispatched</span></div></TableHead>
                      <TableHead className="text-xs text-center w-[60px]">Total Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getTrackingFiltered().length === 0 ? (
                      <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground text-sm">No orders found for this period</TableCell></TableRow>
                    ) : getTrackingFiltered().map(o => (
                      <TableRow key={o.id} className={o.status === 'dispatched' ? 'bg-green-50/50' : o.warehouse_status === 'preparing' ? 'bg-amber-50/50' : ''}>
                        <TableCell className="text-xs font-mono font-semibold">{o.order_number}</TableCell>
                        <TableCell className="text-xs truncate max-w-[120px]">{o.customer_name}</TableCell>
                        <TableCell className="text-xs text-right font-bold">{o.qty}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="outline" className={`text-[9px] ${
                            o.status === 'dispatched' ? 'bg-green-100 text-green-800 border-green-300' :
                            o.status === 'ready_to_dispatch' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                            o.warehouse_status === 'preparing' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {o.status === 'dispatched' ? 'Done' : o.status === 'ready_to_dispatch' ? 'Ready' : o.warehouse_status === 'preparing' ? 'Prep' : 'Sort'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.sent_to_sort_at)}</TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.preparing_at)}</TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.ready_at)}</TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.sequenced_at)}</TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.dispatched_at)}</TableCell>
                        <TableCell className="text-[10px] text-center font-medium">
                          {fmtDuration(o.sent_to_sort_at, o.dispatched_at || o.ready_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Grade Column Placement Dialog */}
      <Dialog open={gradeColumnDialog} onOpenChange={setGradeColumnDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Sheet className="w-5 h-5 text-amber-600" />
              Assign Column for New Grade(s)
            </DialogTitle>
            <DialogDescription>
              The following grade(s) were not found in the Google Sheet. Enter the column letter where each should be placed. If the column has existing data, it will be shifted right.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {unmappedGrades.map((item, idx) => {
              const key = `${item.sku}-${item.grade}`;
              return (
                <div key={idx} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`grade-column-input-${idx}`}>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{item.grade}</p>
                    <p className="text-xs text-muted-foreground">SKU: {item.sku} | Qty: {item.qty}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Column:</Label>
                    <Input
                      value={columnInputs[key] || ''}
                      onChange={(e) => setColumnInputs(prev => ({ ...prev, [key]: e.target.value.toUpperCase() }))}
                      placeholder="e.g. BN"
                      className="w-20 h-8 text-sm font-mono uppercase"
                      maxLength={3}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setGradeColumnDialog(false)} disabled={placingGrades}>
              Skip
            </Button>
            <Button onClick={submitGradeColumnPlacements} disabled={placingGrades} className="gap-1" data-testid="submit-grade-columns-btn">
              {placingGrades ? <><Loader2 className="w-4 h-4 animate-spin" /> Placing...</> : 'Place Grades'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partial Dispatch Dialog for Dispatchable Orders */}
      <Dialog open={partialDialogOpen} onOpenChange={setPartialDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              Partial Dispatch — {partialOrder?.order_number}
              <Badge variant="outline" className="text-xs">{partialOrder?.customer_name}</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-amber-50">
                  <TableHead className="text-[10px] py-1.5 font-bold">Grade</TableHead>
                  <TableHead className="text-[10px] py-1.5 font-bold">SKU</TableHead>
                  <TableHead className="text-[10px] py-1.5 font-bold text-center">Order Qty</TableHead>
                  <TableHead className="text-[10px] py-1.5 font-bold text-center">Dispatch Qty</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partialItems.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="py-1.5 text-xs font-semibold">{item.grade}</TableCell>
                    <TableCell className="py-1.5 text-xs">{item.sku}</TableCell>
                    <TableCell className="py-1.5 text-xs text-center">{item.maxQty}</TableCell>
                    <TableCell className="py-1 text-center">
                      <Input type="number" min={0} max={item.maxQty}
                        value={item.dispatchQty}
                        onChange={e => {
                          const v = Math.min(parseInt(e.target.value) || 0, item.maxQty);
                          setPartialItems(prev => prev.map((p, i) => i === idx ? {...p, dispatchQty: v} : p));
                        }}
                        className="h-7 w-16 text-xs text-center mx-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => setPartialDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={dispSubmitPartial} disabled={sendingPartial} className="gap-1 bg-amber-600 hover:bg-amber-700">
                {sendingPartial ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                Send to Sort
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ledger Dialog for Dispatchable Outstanding */}
      <LedgerDialog open={dispLedgerOpen} onOpenChange={setDispLedgerOpen} initialParty={dispLedgerParty} />

      {/* Activity Log Popup — Full width */}
      <Dialog open={!!activityLogCustomer} onOpenChange={(open) => { if (!open) setActivityLogCustomer(null); }}>
        <DialogContent className="max-w-[95vw] w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Activity Log — {activityLogCustomer?.name}</DialogTitle>
          </DialogHeader>
          {activityLogCustomer && <CustomerActivityLog filterCustomerName={activityLogCustomer.name} />}
        </DialogContent>
      </Dialog>

      {/* Login Sessions Popup — lightweight sessions-only view */}
      <Dialog open={!!loginSessionsPopup} onOpenChange={(open) => { if (!open) setLoginSessionsPopup(null); }}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto" data-testid="login-sessions-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
              <Clock className="w-4 h-4 text-green-600" />
              Login Sessions — {loginSessionsPopup?.name}
            </DialogTitle>
          </DialogHeader>
          {loginSessionsPopup && (() => {
            const acts = [...(loginSessionsPopup.activities || [])].reverse();
            const sessions = [];
            let cur = null;
            for (const a of acts) {
              if (a.action === 'login') { if (cur) sessions.push(cur); cur = { login_time: a.timestamp, actions: [] }; }
              if (cur) cur.actions.push(a);
            }
            if (cur) sessions.push(cur);
            sessions.reverse();
            return (
              <div className="space-y-3 mt-2">
                <p className="text-xs text-muted-foreground">{sessions.length} session{sessions.length !== 1 ? 's' : ''} today</p>
                {sessions.map((session, si) => {
                  const isLatest = si === 0;
                  const loginIST = (() => {
                    if (!session.login_time) return '';
                    const d = new Date(session.login_time);
                    const ist = new Date(d.getTime() + 5.5*60*60*1000);
                    return `${String(ist.getUTCHours()).padStart(2,'0')}:${String(ist.getUTCMinutes()).padStart(2,'0')}`;
                  })();
                  return (
                    <details key={si} open={isLatest} className="border rounded-lg overflow-hidden group">
                      <summary className="bg-green-50 px-3 py-2 border-b flex items-center gap-2 cursor-pointer list-none select-none hover:bg-green-100 transition-colors">
                        <ChevronDown className="w-3.5 h-3.5 text-green-600 transition-transform group-open:rotate-180" />
                        <span className="text-xs font-bold text-green-800">
                          {isLatest ? 'Latest Session' : `Session ${sessions.length - si}`} — {loginIST}
                        </span>
                        <Badge variant="outline" className="text-[9px] ml-auto">{session.actions.length} actions</Badge>
                      </summary>
                      <div className="divide-y">
                        {session.actions.map((a, ai) => {
                          const tIST = (() => {
                            if (!a.timestamp) return '';
                            const d = new Date(a.timestamp);
                            const ist = new Date(d.getTime() + 5.5*60*60*1000);
                            return `${String(ist.getUTCHours()).padStart(2,'0')}:${String(ist.getUTCMinutes()).padStart(2,'0')}`;
                          })();
                          const actionLabel = { login: 'Login', view_orders: 'Viewed Orders', view_ledger: 'Viewed Ledger', view_requests: 'Viewed Requests', search_grade: 'Searched Grade', submit_request: 'Submitted Request', edit_rate: 'Edited Rate', edit_qty: 'Edited Qty', delete_grade: 'Deleted Grade', view_new_order: 'New Order Tab', click_freq_grade: 'Freq Grade', click_high_stock_grade: 'High Stock Grade', click_discounted_grade: 'Discounted Grade', view_profile: 'Viewed Profile', change_password: 'Changed Password', click_refresh: 'Refreshed', view_dispatch: 'Viewed Dispatch', view_invoice: 'Viewed Invoice', view_rate: 'Viewed Rate' }[a.action] || a.action;
                          const isRateView = a.action === 'view_rate';
                          return (
                            <div key={ai} className={`px-3 py-1.5 flex items-start gap-2 text-[11px] ${isRateView ? 'bg-blue-50/50' : ''}`}>
                              <span className="font-mono text-muted-foreground w-12 shrink-0">{tIST}</span>
                              <span className={`font-semibold ${isRateView ? 'text-blue-700' : 'text-gray-700'}`}>{actionLabel}</span>
                              {a.details && <span className={`truncate ${isRateView ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>{a.details}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  );
                })}
                {sessions.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No login sessions found</p>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>


      {/* Customer Preview Popup — renders CustomerDashboard directly */}
      <Dialog open={!!previewCustomer} onOpenChange={(open) => { if (!open) setPreviewCustomer(null); }}>
        <DialogContent className="max-w-[95vw] w-full max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="text-base">Customer Preview — {previewCustomer?.name}</DialogTitle>
          </DialogHeader>
          {previewCustomer && (
            <div className="overflow-y-auto" style={{ maxHeight: '80vh' }}>
              <CustomerDashboard previewCustomerName={previewCustomer.name} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deal Reject/Cancel with Remark Dialog */}
      <Dialog open={dealRejectOpen} onOpenChange={setDealRejectOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Cancel Deal — {dealRejectTarget?.enquiry_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Add a remark (optional) — visible to the customer in their Deals tab.</p>
            <Textarea value={dealRejectRemark} onChange={e => setDealRejectRemark(e.target.value)}
              placeholder="Reason for cancellation..." className="text-xs min-h-[60px]" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setDealRejectOpen(false); setDealRejectRemark(""); }}>Cancel</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={async () => {
                if (!dealRejectTarget) return;
                try {
                  await axios.put(`${API}/order-requests/${dealRejectTarget.id}/reject`, { rejected_by: 'owner', reason: dealRejectRemark });
                  toast.success(`${dealRejectTarget.enquiry_number} cancelled`);
                  setDealRejectOpen(false);
                  setDealRejectRemark("");
                  setDealRejectTarget(null);
                  setSelectedDeal(null);
                  fetchEnquiries();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}>Confirm Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Behaviour Profile Dialog */}
      <Dialog open={!!behaviourProfile} onOpenChange={(open) => { if (!open) { saveBehaviourProfile(); setBehaviourProfile(null); } }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="behaviour-profile-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Customer Profile — {behaviourProfile?.customer_name}</DialogTitle>
          </DialogHeader>
          {behaviourProfile && (() => {
            const s = behaviourProfile.stats || {};
            const TAGS = ["Quick Closer", "Hard Negotiator", "Price Sensitive", "Volume Buyer", "Seasonal Buyer", "Loyal Customer"];
            return (
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <p className="text-[8px] text-muted-foreground">Deals</p>
                    <p className="text-sm font-bold text-blue-800">{s.total_deals || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2 text-center">
                    <p className="text-[8px] text-muted-foreground">Accept %</p>
                    <p className="text-sm font-bold text-green-800">{s.acceptance_rate || 0}%</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <p className="text-[8px] text-muted-foreground">Avg Rounds</p>
                    <p className="text-sm font-bold text-amber-800">{s.avg_rounds || 0}</p>
                  </div>
                </div>

                {/* Rate Preference */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Rate Preference (auto-applied to customer)</p>
                  <div className="flex gap-1.5">
                    {['CR', 'BSR-1', 'BSR-2', 'BSR-3'].map(pref => (
                      <button key={pref} onClick={() => setBehaviourProfile(prev => ({...prev, rate_preference: pref}))}
                        className={`text-[10px] font-bold px-3 py-1.5 rounded-lg border transition-colors ${behaviourProfile.rate_preference === pref ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                        {pref}
                      </button>
                    ))}
                  </div>
                  {behaviourProfile.rate_preference && <p className="text-[9px] text-emerald-600 mt-1">Customer will see {behaviourProfile.rate_preference} rates by default</p>}
                </div>

                {/* Customer Display Preferences */}
                <div className="border rounded-lg p-3 space-y-3 bg-gray-50/50">
                  <p className="text-[10px] font-bold text-muted-foreground">Customer New Order Display</p>
                  
                  {/* Toggle: Highest Selling Grades */}
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-highest-selling">
                    <input type="checkbox" checked={!!behaviourProfile.show_highest_selling}
                      onChange={e => setBehaviourProfile(prev => ({...prev, show_highest_selling: e.target.checked}))}
                      className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
                    <span className="text-[11px] font-semibold text-gray-700">Show Highest Selling Grades</span>
                    <span className="text-[9px] text-muted-foreground ml-auto">Grade + SKU</span>
                  </label>

                  {/* Toggle: Discounted Grades */}
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-discounted-grades">
                    <input type="checkbox" checked={!!behaviourProfile.show_discounted_grades}
                      onChange={e => setBehaviourProfile(prev => ({...prev, show_discounted_grades: e.target.checked}))}
                      className="w-4 h-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500" />
                    <span className="text-[11px] font-semibold text-gray-700">Show Discounted Grades</span>
                  </label>

                  {/* Discounted Grades Entries */}
                  {behaviourProfile.show_discounted_grades && (
                    <div className="space-y-2 pl-1">
                      <p className="text-[9px] text-muted-foreground">Add grades with original & offered rates:</p>
                      {(behaviourProfile.discounted_grades || []).map((dg, idx) => (
                        <div key={idx} className="flex items-center gap-1.5" data-testid={`discounted-grade-row-${idx}`}>
                          <Select value={dg.grade || ''} onValueChange={v => {
                            const updated = [...(behaviourProfile.discounted_grades || [])];
                            updated[idx] = {...updated[idx], grade: v};
                            setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                          }}>
                            <SelectTrigger className="h-7 text-[10px] w-[90px] px-1.5"><SelectValue placeholder="Grade" /></SelectTrigger>
                            <SelectContent className="max-h-[200px]">
                              {(products || []).map(p => p.grade).filter((v, i, a) => v && a.indexOf(v) === i).sort().map(g => (
                                <SelectItem key={g} value={g} className="text-xs">{g}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={dg.sku || 'BUCKET'} onValueChange={v => {
                            const updated = [...(behaviourProfile.discounted_grades || [])];
                            updated[idx] = {...updated[idx], sku: v};
                            setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                          }}>
                            <SelectTrigger className="h-7 text-[10px] w-[72px] px-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BUCKET" className="text-xs">BUCKET</SelectItem>
                              <SelectItem value="TIN" className="text-xs">TIN</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input type="number" value={dg.original_rate || ''} placeholder="Rate ₹"
                            onChange={e => {
                              const updated = [...(behaviourProfile.discounted_grades || [])];
                              updated[idx] = {...updated[idx], original_rate: e.target.value ? parseFloat(e.target.value) : ''};
                              setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                            }}
                            className="h-7 text-[10px] w-[70px] px-1.5 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                          <span className="text-[10px] text-muted-foreground">→</span>
                          <Input type="number" value={dg.offered_rate || ''} placeholder="Offer ₹"
                            onChange={e => {
                              const updated = [...(behaviourProfile.discounted_grades || [])];
                              updated[idx] = {...updated[idx], offered_rate: e.target.value ? parseFloat(e.target.value) : ''};
                              setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                            }}
                            className="h-7 text-[10px] w-[70px] px-1.5 text-center border-green-300 bg-green-50/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                          <button onClick={() => {
                            const updated = (behaviourProfile.discounted_grades || []).filter((_, i) => i !== idx);
                            setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                          }} className="text-red-400 hover:text-red-600 p-0.5" data-testid={`remove-discounted-${idx}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => {
                        const updated = [...(behaviourProfile.discounted_grades || []), { grade: '', sku: 'BUCKET', original_rate: '', offered_rate: '' }];
                        setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                      }} className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-semibold"
                        data-testid="add-discounted-grade">
                        <Plus className="w-3 h-3" /> Add Grade
                      </button>
                    </div>
                  )}
                </div>

                {/* Bill Type */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Default Bill Type</p>
                  <div className="flex gap-1.5">
                    {['EST', 'INV'].map(bt => (
                      <button key={bt} onClick={() => setBehaviourProfile(prev => ({...prev, bill_type: bt}))}
                        className={`text-[10px] font-bold px-3 py-1.5 rounded-lg border transition-colors ${behaviourProfile.bill_type === bt ? 'bg-amber-600 text-white border-amber-600' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                        {bt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment Terms */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Default Payment Terms</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {['ADV', 'RDAP', 'DDAP', 'DDAP RECVD', 'REG'].map(pt => (
                      <button key={pt} onClick={() => setBehaviourProfile(prev => ({...prev, payment_terms: pt}))}
                        className={`text-[10px] font-bold px-2.5 py-1 rounded-lg border transition-colors ${behaviourProfile.payment_terms === pt ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                        {pt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Round Power */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Negotiation Round Power</p>
                  <Input value={behaviourProfile.round_power || ''} onChange={e => setBehaviourProfile(prev => ({...prev, round_power: e.target.value}))}
                    placeholder="e.g., 2-3 rounds typical" className="h-8 text-xs" />
                </div>

                {/* Credit Limit */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Credit Limit (₹)</p>
                  <Input type="number" data-testid="credit-limit-input" value={behaviourProfile.credit_limit ?? 2000000}
                    onChange={e => setBehaviourProfile(prev => ({...prev, credit_limit: e.target.value ? parseFloat(e.target.value) : 0}))}
                    placeholder="Default: 20,00,000" className="h-8 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                  <p className="text-[8px] text-muted-foreground mt-0.5">Default: ₹20,00,000. Customer sees warning if debit exceeds this.</p>
                </div>

                {/* Tags */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Behaviour Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {TAGS.map(tag => (
                      <button key={tag} onClick={() => {
                        const tags = behaviourProfile.tags || [];
                        setBehaviourProfile(prev => ({...prev, tags: tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag]}));
                      }} className={`text-[9px] px-2 py-1 rounded-full border transition-colors ${(behaviourProfile.tags || []).includes(tag) ? 'bg-blue-100 border-blue-400 text-blue-800 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Notes</p>
                  <Textarea value={behaviourProfile.notes || ''} onChange={e => setBehaviourProfile(prev => ({...prev, notes: e.target.value}))}
                    placeholder="Observations..." className="text-xs min-h-[40px]" />
                </div>

                {/* Suggestion */}
                {(behaviourProfile.rate_preference || behaviourProfile.show_highest_selling || behaviourProfile.show_discounted_grades) && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                    <p className="text-[9px] text-emerald-700">
                      {behaviourProfile.rate_preference && <>Auto-rate: <b>{behaviourProfile.rate_preference}</b> | </>}
                      Bill: <b>{behaviourProfile.bill_type || 'Not set'}</b> | Pay: <b>{behaviourProfile.payment_terms || 'Not set'}</b>
                      {behaviourProfile.show_highest_selling && <> | <b>Highest Selling</b> ✓</>}
                      {behaviourProfile.show_discounted_grades && <> | <b>Discounted</b> ({(behaviourProfile.discounted_grades || []).length})</>}
                    </p>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

    </div>
  );
}
